// src/pages/client_pets/AddNewPet.js

import React, { useEffect, useState, useCallback, useRef } from "react";
import { Avatar } from "@mui/material";
import { HiArrowSmLeft } from "react-icons/hi";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import { Autocomplete, Chip, TextField, Typography } from "@mui/material";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import PetsIcon from "@mui/icons-material/Pets";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { Upload, X, Plus, ChevronDown, FileText, ExternalLink, Trash2 } from "lucide-react"; // for file upload UI
import { uploadFileToS3 } from "../../components/common/uploadService";
import config from "../../config";

const S3_BASE = config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL;

/* ---------------- KNOWN NATIVE PET MAPS_TO KEYS ---------------- */
const KNOWN_NATIVE_PET_MAPS_TO = new Set([
  // Basic Info
  "pet.name",
  "pet.pet_type",
  "pet.type",
  "pet.breed",
  "pet.size",
  "pet.gender",
  "pet.weight_kg",
  "pet.birthday",
  "pet.age",
  "pet.neutered_status",
  "pet.neutered_spayed",
  "pet.photo",
  "pet.image",
  "pet.tags",
  // Appearance
  "pet.hair_type",
  "pet.hair_length",
  "pet.hair_color",
  "pet.color",
  // Behavior
  "pet.behaviour",
  "pet.behavior_notes",
  // Health & Medical
  "pet.has_medical_condition",
  "pet.medical_conditions",
  "pet.medical_condition",
  "pet.custom_medical_condition",
  "pet.allergies",
  // Medication
  "pet.on_medication",
  "pet.medication_name",
  "pet.medication_time",
  "pet.medication_dosage",
  "pet.medication_dosage_unit",
  "pet.medication_instructions",
  "pet.medication_photo",
  "pet.ongoing_medications",
  // Vaccination
  "pet.vaccination_status",
  "pet.vaccinations",
  "pet.vaccination_records",
  "pet.rabies_vaccination_date",
  "pet.rabies_vaccination_image",
  "pet.dhppi_vaccination_date",
  "pet.dhppi_vaccination_image",
  // Diet & Feeding
  "pet.meal_type",
  "pet.meals_per_day",
  "pet.what_to_feed",
  "pet.when_to_feed",
  "pet.how_much_to_feed",
  "pet.feeding_instructions",
  "pet.meals",
  "pet.walks_per_day",
  "pet.custom_meal_notes",
  "pet.hydration",
  // Special Instructions & Other
  "pet.special_care_instructions",
  "pet.veterinarian",
  "pet.signature",
  "pet.documents",
]);

const isNativePetField = (mapsTo = "") => {
  if (!mapsTo) return false;
  if (KNOWN_NATIVE_PET_MAPS_TO.has(mapsTo)) return true;
  if (KNOWN_NATIVE_PET_MAPS_TO.has(`pet.${mapsTo}`)) return true;
  if (mapsTo.endsWith("_vaccination_date") || mapsTo.endsWith("_vaccination_image")) return true;
  if (/^pet\.(.+)_vaccination_(date|image)$/i.test(mapsTo)) return true;
  return false;
};

const isCustomerScopeField = (mapsTo = "", sectionKey = "", sectionName = "") => {
  const m = String(mapsTo || "").toLowerCase();
  const sk = String(sectionKey || "").toLowerCase();
  const sn = String(sectionName || "").toLowerCase();
  return (
    m.startsWith("customer.") ||
    sk.includes("customer") ||
    sk.includes("client") ||
    sk.includes("address") ||
    sk.includes("parent") ||
    sn.includes("customer") ||
    sn.includes("client") ||
    sn.includes("address") ||
    sn.includes("parent")
  );
};

/* ---------------- DATE HELPERS ---------------- */
const parseDateParts = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) {
    if (isNaN(dateStr.getTime())) return null;
    return {
      year: dateStr.getFullYear(),
      month: dateStr.getMonth() + 1,
      day: dateStr.getDate(),
    };
  }
  const cleanStr = String(dateStr).trim().split("T")[0];
  const parts = cleanStr.split(/[-/]/).map(Number);
  if (parts.length === 3 && parts.every((n) => !isNaN(n))) {
    if (String(parts[0]).length === 4) {
      // YYYY-MM-DD
      return { year: parts[0], month: parts[1], day: parts[2] };
    }
    if (String(parts[2]).length === 4) {
      // DD-MM-YYYY
      return { year: parts[2], month: parts[1], day: parts[0] };
    }
  }
  const dt = new Date(dateStr);
  if (!isNaN(dt.getTime())) {
    return {
      year: dt.getFullYear(),
      month: dt.getMonth() + 1,
      day: dt.getDate(),
    };
  }
  return null;
};

const parseDateToTime = (val) => {
  const p = parseDateParts(val);
  if (!p) return null;
  const dt = new Date(p.year, p.month - 1, p.day);
  return isNaN(dt.getTime()) ? null : dt.getTime();
};

const formatDate = (dateStr) => {
  const p = parseDateParts(dateStr);
  if (!p) return typeof dateStr === "string" && dateStr.trim() ? dateStr : "—";
  const dd = String(p.day).padStart(2, "0");
  const mm = String(p.month).padStart(2, "0");
  const yyyy = String(p.year);
  return `${dd}-${mm}-${yyyy}`;
};

/* ---------------- ADD MONTHS HELPER ---------------- */
const addMonths = (dateStr, months) => {
  const p = parseDateParts(dateStr);
  if (!p) return "";
  const d = new Date(p.year, p.month - 1, p.day);
  d.setMonth(d.getMonth() + months);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const isExpired = (dateStr) => {
  const p = parseDateParts(dateStr);
  if (!p) return false;

  const today = new Date();
  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  return new Date(p.year, p.month - 1, p.day) < todayDate;
};

/* ---------------- FILE VIEWER MODAL (reused) ---------------- */
const FileViewerModal = ({ url, onClose }) => {
  if (!url) return null;
  const isPdf =
    url.toLowerCase().includes(".pdf") ||
    url.toLowerCase().includes("application/pdf");
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-xl w-[95%] max-w-3xl h-[85%] shadow-xl flex flex-col overflow-hidden">
        <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50">
          <h2 className="text-lg font-semibold">
            {isPdf ? "PDF Viewer" : "Image Preview"}
          </h2>
          <button onClick={onClose} className="text-gray-700 hover:text-black text-4xl leading-none">
            ×
          </button>
        </div>
        <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-100">
          {isPdf ? (
            <iframe src={url} title="PDF Viewer" className="w-full h-full" />
          ) : (
            <img src={url} className="max-w-full max-h-full object-contain" alt="Preview" />
          )}
        </div>
      </div>
    </div>
  );
};
const DEFAULT_VACCINATION_OPTIONS = [
  "Parvovirus",
  "Hepatitis",
  "Distemper",
  "Rabies",
  "Parainfluenza",
  "Bordetella",
  "DHPP",
];

const DEFAULT_PREDEFINED_PET_TAGS = [
  "Senior",
  "Puppy",
  "Disabled",
  "Sensitive",
  "Pregnant",
  "Friendly",
  "Anxious",
  "Good with kids",
  "Not good with kids",
  "Good with other pets",
  "Not good with other pets",
  "Calm",
  "Timid",
  "Nervous",
];

const DEFAULT_HAIR_COLOR_OPTIONS = [
  { name: "White", hex: "#FFFFFF" },
  { name: "Black", hex: "#000000" },
  { name: "Brown", hex: "#795548" },
  { name: "Golden", hex: "#F4B400" },
  { name: "Grey", hex: "#808080" },
  { name: "Orange", hex: "#F59E0B" },
  { name: "Cream", hex: "#FFF5DC" },
  { name: "Tan", hex: "#D2B48C" },
  { name: "Silver", hex: "#C0C0C0" },
  { name: "Fawn", hex: "#E5AA70" },
];

const AddNewPet = ({
  isOpen,
  onClose,
  onSave,
  clientId,
  existingPets = [],
  onBack = null,
  onSaveAndContinue = null,
  petId = null,
  onEditSave = null,
  isFullWidth = false,
  shouldHideSaveAndAdd = false,
  vendorId, // NEW: required for file uploads
}) => {
  const [vendorCustomTags, setVendorCustomTags] = useState([]);
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [addingTagLoading, setAddingTagLoading] = useState(false);
  const [fetchingTagsLoading, setFetchingTagsLoading] = useState(false);

  const [petTypes, setPetTypes] = useState([]);
  const [petSizes, setPetSizes] = useState([]);
  const [petBreeds, setPetBreeds] = useState([]);
  const [ageMode, setAgeMode] = useState("dob");
  const [newPet, setNewPet] = useState({
    type: "",
    name: "",
    gender: "",
    size: "",
    breed: "",
    weight_kg: "",
    age: "",
    birthday: "",
    behavior_notes: "",
    tags: [],
    medical_conditions: [],
    vaccinations: [],           // will be derived from vaccinationRecords
    vaccination_records: [],    // NEW: array of {vaccine, date_given, next_due, documents}
    hair_type: "",
    hair_length: "",
    color: [],
    meals_per_day: "",
    walks_per_day: "",
    meal_type: [],
    neutered_status: "",
    ongoing_medications: [],
    meals: [],
    meal_history: [],
    custom_fields: {},
  });
  const [healthTab, setHealthTab] = useState("vaccination");
  const [medicalConditionQuery, setMedicalConditionQuery] = useState("");
  const [medicalConditionListOpen, setMedicalConditionListOpen] = useState(true);
  const [vaccinationOptions, setVaccinationOptions] = useState(
    DEFAULT_VACCINATION_OPTIONS,
  );
  const [vaccineSearchQuery, setVaccineSearchQuery] = useState("");
  const [vaccineDropdownOpen, setVaccineDropdownOpen] = useState(false);
  const [addingVaccineLoading, setAddingVaccineLoading] = useState(false);
  const vaccineDropdownRef = useRef(null);
  const [medicalConditionOptions, setMedicalConditionOptions] = useState([]);
  const [medicalConditionObjects, setMedicalConditionObjects] = useState([]); // stores { _id, name, pet_type }

  const filteredVaccineOptions = React.useMemo(() => {
    const query = vaccineSearchQuery.trim().toLowerCase();
    if (!query) return vaccinationOptions;
    return vaccinationOptions.filter((opt) =>
      opt.toLowerCase().includes(query),
    );
  }, [vaccinationOptions, vaccineSearchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        vaccineDropdownRef.current &&
        !vaccineDropdownRef.current.contains(event.target)
      ) {
        setVaccineDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Merge any vaccines from existing pet records into vaccinationOptions
  useEffect(() => {
    if (newPet.vaccination_records && newPet.vaccination_records.length > 0) {
      const recordVaccines = newPet.vaccination_records
        .map((r) => r.vaccine || r.name)
        .filter(Boolean);
      if (recordVaccines.length > 0) {
        setVaccinationOptions((prev) => {
          const combined = [...new Set([...prev, ...recordVaccines])];
          return combined.length !== prev.length ? combined : prev;
        });
      }
    }
  }, [newPet.vaccination_records]);
  const hairTypeOptions = [
    "Smooth",
    "Wire",
    "Double",
    "Hairless",
    "Curly",
    "Wavy",
    "Drop Coat",
  ];
  const hairLengthOptions = ["Short Hair", "Medium Hair", "Long Hair"];
  const hairColorOptions = DEFAULT_HAIR_COLOR_OPTIONS;
  const mealTypeOptions = [
    "Vegetarian",
    "Non-Vegetarian",
    "Home-Cooked Diet",
    "Mixed Diet",
    "Prescription Diet",
    "Raw Diet",
  ];

  // Medication and Meal Drawer Management States
  const [showAddMedForm, setShowAddMedForm] = useState(false);
  const [newMedData, setNewMedData] = useState({
    name: "",
    dosage: "",
    time: "Morning",
    duration: "",
    notes: "",
    active: true,
  });

  const [showAddMealForm, setShowAddMealForm] = useState(false);
  const [newMealData, setNewMealData] = useState({
    name: "",
    time: "Morning",
    quantity: "",
    notes: "",
  });

  const handleAddNewMedication = () => {
    if (!newMedData.name?.trim()) {
      toast.warn("Medication name is required", { position: "top-center" });
      return;
    }
    const medItem = {
      name: newMedData.name.trim(),
      dosage: newMedData.dosage.trim(),
      time: newMedData.time.trim(),
      duration: newMedData.duration.trim(),
      notes: newMedData.notes.trim(),
      active: newMedData.active !== false,
      documents: [],
      start_date: new Date(),
    };
    setNewPet((prev) => ({
      ...prev,
      ongoing_medications: [...(prev.ongoing_medications || []), medItem],
    }));
    setNewMedData({
      name: "",
      dosage: "",
      time: "Morning",
      duration: "",
      notes: "",
      active: true,
    });
    setShowAddMedForm(false);
  };

  const handleToggleMedicationStatus = (idx) => {
    setNewPet((prev) => {
      const list = [...(prev.ongoing_medications || [])];
      if (!list[idx]) return prev;
      const isCurrentlyActive = list[idx].active !== false;
      list[idx] = {
        ...list[idx],
        active: !isCurrentlyActive,
        end_date: isCurrentlyActive ? new Date() : null,
      };
      return { ...prev, ongoing_medications: list };
    });
  };

  const handleDeleteMedication = (idx) => {
    setNewPet((prev) => {
      const list = [...(prev.ongoing_medications || [])];
      list.splice(idx, 1);
      return { ...prev, ongoing_medications: list };
    });
  };

  const handleAddNewMeal = () => {
    if (!newMealData.name?.trim()) {
      toast.warn("Meal name is required", { position: "top-center" });
      return;
    }
    const mealItem = {
      name: newMealData.name.trim(),
      time: newMealData.time.trim(),
      quantity: newMealData.quantity.trim(),
      notes: newMealData.notes.trim(),
      added_date: new Date().toISOString(),
    };
    setNewPet((prev) => ({
      ...prev,
      meals: [...(prev.meals || []), mealItem],
    }));
    setNewMealData({
      name: "",
      time: "Morning",
      quantity: "",
      notes: "",
    });
    setShowAddMealForm(false);
  };

  const handleDeleteMeal = (idx) => {
    setNewPet((prev) => {
      const list = [...(prev.meals || [])];
      list.splice(idx, 1);
      return { ...prev, meals: list };
    });
  };

  // Dynamic Form Configuration & Custom Fields
  const [formConfig, setFormConfig] = useState({
    labelMap: {},
    petCustomFields: [],
  });

  const fetchFormConfig = useCallback(async () => {
    try {
      const res = await httpService.get("/vendors/getAllForms");
      const forms = res.data?.data || res.data || [];
      if (!Array.isArray(forms) || forms.length === 0) return;

      const labelMap = {};
      const petCustom = [];

      const corePetKeys = new Set([
        "pet.name",
        "pet.pet_type",
        "pet.type",
        "pet.breed",
        "pet.size",
        "pet.gender",
        "pet.weight_kg",
        "pet.birthday",
        "pet.age",
        "pet.behavior_notes",
        "pet.neutered_status",
        "pet.hair_type",
        "pet.hair_length",
        "pet.color",
        "pet.photo",
        "pet.image",
      ]);

      const seenQ = new Set();
      forms.forEach((f) => {
        if (!Array.isArray(f.questions)) return;
        f.questions.forEach((q) => {
          if (q.enabled === false) return;
          const aType = String(q.answerType || "").toLowerCase();
          if (aType === "section_heading" || aType === "section_header") return;

          const mapsTo = String(q.maps_to || q.mapsTo || "custom").trim();
          const label = String(q.question || q.label || "").trim();
          const sectionKey = String(
            q.section_key || q.section || "",
          ).toLowerCase();
          const sectionName = String(
            q.sectionName || q.section_name || "",
          ).toLowerCase();

          // 1. If it maps to a standard/native pet field, register in labelMap and DO NOT add to petCustomFields
          if (isNativePetField(mapsTo)) {
            if (label) labelMap[mapsTo] = label;
            return;
          }

          // 2. Exclude any customer / parent / address questions from Pet drawer
          if (isCustomerScopeField(mapsTo, sectionKey, sectionName)) {
            return;
          }

          // 3. Only true custom pet questions go into petCustomFields
          const isPetScope =
            mapsTo.startsWith("pet.") ||
            sectionKey.includes("pet") ||
            sectionKey.includes("basic") ||
            (sectionName.includes("pet") && !sectionName.includes("parent")) ||
            sectionName.includes("basic");

          if (isPetScope) {
            const uid = String(q.field_uid || q._id || q.question);
            if (!seenQ.has(uid)) {
              seenQ.add(uid);
              petCustom.push({
                _id: q._id,
                field_uid: q.field_uid || q.fieldUid || String(q._id),
                label: label || "Custom Field",
                answerType: q.answerType || "text",
                options: q.options || [],
                isRequired: q.isRequired || false,
                placeholder: q.placeholder || "",
              });
            }
          }
        });
      });

      setFormConfig({
        labelMap,
        petCustomFields: petCustom,
      });
    } catch (err) {
      console.error("Error loading dynamic form questions for pet:", err);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchFormConfig();
    }
  }, [isOpen, fetchFormConfig]);

  const getPetCustomFieldValue = (field) => {
    const cf = newPet.custom_fields || {};
    if (field.field_uid && cf[field.field_uid] !== undefined)
      return cf[field.field_uid];
    if (field._id && cf[field._id] !== undefined) return cf[field._id];
    if (field.label && cf[field.label] !== undefined) return cf[field.label];
    return "";
  };

  const handlePetCustomFieldChange = (field, value) => {
    const key = field.field_uid || field._id || field.label;
    setNewPet((prev) => ({
      ...prev,
      custom_fields: {
        ...(prev.custom_fields || {}),
        [key]: value,
      },
    }));
  };

  // ---- Vaccination drawer state ----
  const [showVaccineDrawer, setShowVaccineDrawer] = useState(false);
  const [vaccineDrawerMode, setVaccineDrawerMode] = useState("record"); // "record" | "createMaster"
  const [newMasterVaccineName, setNewMasterVaccineName] = useState("");
  const [currentVaccineRecord, setCurrentVaccineRecord] = useState(null);
  const [isDraggingVaccineFile, setIsDraggingVaccineFile] = useState(false);
  const vaccineFileInputRef = useRef(null);
  const [fileViewUrl, setFileViewUrl] = useState(null);

  const normalizeStringArray = (value) => {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return (
            item.name ||
            item.label ||
            item.value ||
            item.vaccine ||
            item.vaccination ||
            ""
          )
            .toString()
            .trim();
        }
        return "";
      })
      .filter(Boolean);
  };

  const extractVaccinationNames = (data = {}) => {
    const fromVaccinations = normalizeStringArray(data.vaccinations);
    const fromRecords = Array.isArray(data.vaccination_records)
      ? data.vaccination_records
          .map((item) =>
            typeof item === "string"
              ? item
              : item?.vaccine || item?.name || item?.label || "",
          )
          .map((v) => (v || "").toString().trim())
          .filter(Boolean)
      : [];
    return Array.from(new Set([...fromVaccinations, ...fromRecords]));
  };

  const [petFormErrors, setPetFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [allPetSizes, setAllPetSizes] = useState([]);
  const [formReady, setFormReady] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [customColor, setCustomColor] = useState("#000000");

  // ---- Profile Image States ----
  const [profileImagePreview, setProfileImagePreview] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [isImageChanged, setIsImageChanged] = useState(false);

  const originalPetDataRef = useRef(null);
  const hydratedPetIdRef = useRef(null);

  // ---- Image Handlers ----
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);
    setIsImageChanged(true);

    try {
      const uploadedUrl = await uploadFileToS3(file, {
        module: "client-pets",
        docType: "profile",
        returnType: "fullUrl",
      });

      setProfileImagePreview(uploadedUrl);
      setUploadedImageUrl(uploadedUrl);
    } catch (error) {
      console.error("Pet image upload failed:", error);
      setProfileImagePreview("");
      setUploadedImageUrl("");
    } finally {
      e.target.value = "";
      setImageFile(null);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setUploadedImageUrl("");
    setProfileImagePreview("");
    setIsImageChanged(true);
    document.getElementById("pet-image-input").value = "";
  };

  // ---- Color helpers ----
  const getColorHex = (colorStr) => {
    const found = hairColorOptions.find((opt) => opt.name === colorStr);
    return found ? found.hex : colorStr;
  };

  const addCustomColor = () => {
    const currentColors = Array.isArray(newPet.color) ? newPet.color : [];
    if (!currentColors.includes(customColor)) {
      handleFieldChange("color", [...currentColors, customColor]);
    }
  };

  const removeColor = (colorToRemove) => {
    const currentColors = Array.isArray(newPet.color) ? newPet.color : [];
    handleFieldChange(
      "color",
      currentColors.filter((c) => c !== colorToRemove),
    );
  };

  // ---- Derived state ----
  const isDogOrCat = React.useMemo(() => {
    const selectedType = petTypes.find((t) => t._id === newPet.type);
    if (!selectedType) return false;
    return ["dog", "cat"].includes(selectedType.name.toLowerCase());
  }, [newPet.type, petTypes]);

  const [ageYears, setAgeYears] = useState("");
  const [ageMonths, setAgeMonths] = useState("");

  const filteredBreeds = React.useMemo(() => {
    if (!newPet.type || !petBreeds.length) return [];
    return petBreeds.filter((breed) => breed.pet_type_id === newPet.type);
  }, [petBreeds, newPet.type]);

  // ---- Age helpers ----
  const updateAgeDisplay = (y, m) => {
    const parts = [];
    if (y) parts.push(`${y}y`);
    if (m) parts.push(`${m}m`);
    const display = parts.length ? parts.join(" ") : " ";
    setNewPet((prev) => ({ ...prev, age: display }));
  };

  const handleAgeYearsChange = (e) => {
    const val = e.target.value;
    setAgeMode("age");
    setAgeYears(val);
    updateAgeDisplay(val, ageMonths);
    setNewPet((prev) => ({ ...prev, birthday: "" }));
  };

  const handleAgeMonthsChange = (e) => {
    const val = e.target.value;
    setAgeMode("age");
    setAgeMonths(val);
    updateAgeDisplay(ageYears, val);
    setNewPet((prev) => ({ ...prev, birthday: "" }));
  };

  const calculateAgeFromDOB = (dobString) => {
    if (!dobString) return { years: "", months: "", days: "" };
    const dob = new Date(dobString + "T00:00:00");
    const now = new Date();

    let years = now.getFullYear() - dob.getFullYear();
    let months = now.getMonth() - dob.getMonth();
    let days = now.getDate() - dob.getDate();

    if (days < 0) {
      months--;
      const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      days += lastMonth.getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    return {
      years: Math.max(0, years),
      months: Math.max(0, months),
      days: Math.max(0, days),
    };
  };

  const handleBirthDateChange = (e) => {
    const value = e.target.value;
    if (value) {
      const { years, months } = calculateAgeFromDOB(value);
      setAgeYears(years.toString());
      setAgeMonths(months.toString());
      updateAgeDisplay(years, months);
    } else {
      setAgeYears("");
      setAgeMonths("");
      setAgeMode("dob");
      updateAgeDisplay("", "");
    }
    setNewPet((prev) => ({ ...prev, birthday: value }));
  };

  const filteredPetSizes = React.useMemo(() => {
    if (!newPet.type) return [];
    return allPetSizes.filter((size) => size.pet_type_id === newPet.type);
  }, [allPetSizes, newPet.type]);

  // ---- Validate form ----
  const validateForm = (sizes = filteredPetSizes) => {
    const errors = {};
    if (!newPet.name?.trim()) errors.name = "Pet Name is required";
    if (!newPet.type) errors.type = "Type is required";
    if (!newPet.size) errors.size = "Size is required";
    if (sizes.length > 0 && !newPet.size) errors.size = "Size is required";
    if (isDogOrCat && !newPet.breed)
      errors.breed = "Breed is required for Dogs and Cats";
    return errors;
  };

  /* ---------- SYNC ON EDIT ---------- */
  useEffect(() => {
    if (petId && newPet.age && !newPet.birthday) {
      const matchY = newPet.age.match(/(\d+)y/i);
      const matchM = newPet.age.match(/(\d+)m/i);
      setAgeYears(matchY ? matchY[1] : "");
      setAgeMonths(matchM ? matchM[1] : "");
    }
  }, [petId, newPet.age, newPet.birthday]);

  /* ---------- FETCH PET DETAILS ---------- */
  const getPetDetails = useCallback(
    async (sizesOverride = []) => {
      if (!petId) return;

      try {
        const response = await httpService.get(
          `/vendorpet/getvendorpet/${petId}`,
        );

        const data = response?.data?.data || response?.data || {};
        originalPetDataRef.current = data;

        if (data.image) {
          setProfileImagePreview(data.image);
          setIsImageChanged(false);
        }

        const normalizeMedicalConditions = (value) =>
          Array.isArray(value)
            ? value
                .map((item) => {
                  if (typeof item === "string") return item.trim();
                  if (item && typeof item === "object") {
                    return (
                      item.medical_condition ||
                      item.condition ||
                      item.name ||
                      item.label ||
                      ""
                    )
                      .toString()
                      .trim();
                  }
                  return "";
                })
                .filter(Boolean)
            : typeof value === "string" && value.trim()
              ? [value.trim()]
              : [];

        let overviewMedicalConditions = [];
        if (clientId) {
          try {
            const overviewRes = await httpService.get(
              `/vendorclient/getVendorClientPetsOverviewById/${clientId}`,
            );
            const overviewPets = overviewRes?.data?.data?.pet_details || [];
            const overviewPet = overviewPets.find((pet) => pet._id === petId);
            overviewMedicalConditions = normalizeMedicalConditions(
              overviewPet?.medical_condition ??
                overviewPet?.medical_conditions ??
                overviewPet?.medical_conditon ??
                [],
            );
          } catch (overviewError) {
            console.warn(
              "Failed to load pet overview for medical conditions",
              overviewError,
            );
          }
        }

        const medicalConditions =
          normalizeMedicalConditions(
            data.medical_condition ||
              data.medical_conditions ||
              data.medical_conditon,
          ).length > 0
            ? normalizeMedicalConditions(
                data.medical_condition ||
                  data.medical_conditions ||
                  data.medical_conditon,
              )
            : overviewMedicalConditions;

        let vendorSizeId = "";
        if (data.size?._id) {
          const vendorSize = sizesOverride.find(
            (size) => size.flps_pet_size_id === data.size._id,
          );
          vendorSizeId = vendorSize?._id || "";
        }

        const birthday = data.birthday ? data.birthday.split("T")[0] : "";
        const age = data.age || "";

        // Load vaccination records
        let vaccinationRecords = [];
        if (Array.isArray(data.vaccination_records)) {
          vaccinationRecords = data.vaccination_records.map((rec) => ({
            vaccine: rec.vaccine || "",
            date_given: rec.date_given ? rec.date_given.split("T")[0] : "",
            next_due: rec.next_due ? rec.next_due.split("T")[0] : "",
            documents: Array.isArray(rec.documents)
              ? rec.documents.map((d) => ({
                  name: d.name,
                  url: d.url.startsWith("http") ? d.url : `${S3_BASE}/${d.url}`,
                }))
              : [],
          }));
        }

        setNewPet({
          name: data.name || "",
          type: data.pet_type?._id || "",
          gender: data.gender || "",
          breed: data.breed?._id || "",
          weight_kg: data.weight_kg || "",
          age,
          birthday,
          behavior_notes:
            data.behavior_notes ||
            data.behaviour ||
            (Array.isArray(data.custom_fields?.["pet.behaviour"])
              ? data.custom_fields["pet.behaviour"].join(", ")
              : data.custom_fields?.["pet.behaviour"]?.value
                ? (Array.isArray(data.custom_fields["pet.behaviour"].value)
                    ? data.custom_fields["pet.behaviour"].value.join(", ")
                    : String(data.custom_fields["pet.behaviour"].value))
                : (typeof data.custom_fields?.["pet.behaviour"] === "string"
                    ? data.custom_fields["pet.behaviour"]
                    : "")),
          tags: normalizeStringArray(data.tags),
          medical_conditions: medicalConditions,
          vaccinations: extractVaccinationNames(data),
          vaccination_records: vaccinationRecords,
          hair_type: data.hair_type || "",
          hair_length: data.hair_length || "",
          color: normalizeStringArray(data.color),
          meals_per_day: data.meals_per_day || "",
          walks_per_day: data.walks_per_day || "",
          meal_type: normalizeStringArray(data.meal_type),
          neutered_status:
            data.neutered_status ||
            (data.neutered_spayed === true
              ? "Neutered"
              : data.neutered_spayed === false
                ? "Not Neutered"
                : "Unknown"),
          reference:
            data.reference || data.source || data.reference_source || "",
          size: vendorSizeId,
          ongoing_medications: Array.isArray(data.ongoing_medications)
            ? data.ongoing_medications
            : [],
          meals: Array.isArray(data.meals)
            ? data.meals.map((m) => ({
                ...m,
                added_date: m.added_date || data.createdAt || null,
              }))
            : [],
          meal_history: Array.isArray(data.meal_history)
            ? data.meal_history
            : [],
          custom_fields: data.custom_fields || {},
          createdAt: data.createdAt || null,
        });
        hydratedPetIdRef.current = petId;

        if (birthday) {
          setAgeMode("dob");
          const { years, months } = calculateAgeFromDOB(birthday);
          setAgeYears(years.toString());
          setAgeMonths(months.toString());
        } else if (age) {
          setAgeMode("age");
          const matchY = age.match(/(\d+)y/i);
          const matchM = age.match(/(\d+)m/i);
          setAgeYears(matchY ? matchY[1] : "");
          setAgeMonths(matchM ? matchM[1] : "");
        } else {
          setAgeMode("dob");
          setAgeYears("");
          setAgeMonths("");
        }
      } catch (error) {
        toast.error("Failed to fetch pet details", {
          position: "top-center",
        });
      }
    },
    [petId, clientId],
  );

  // ---- Field change ----
  const handleFieldChange = (field, value) => {
    setNewPet((prev) => ({ ...prev, [field]: value }));
    setPetFormErrors((prev) => {
      if (prev[field]) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return prev;
    });
  };

  useEffect(() => {
    if (healthTab === "medical") {
      setMedicalConditionListOpen(true);
      setMedicalConditionQuery("");
    }
  }, [healthTab]);

// const filteredMedicalConditionOptions = React.useMemo(() => {
//   const query = medicalConditionQuery.trim().toLowerCase();
//   // If a pet type is selected, filter by pet_type, else show all
//   const filtered = medicalConditionObjects.filter(cond => {
//     if (newPet.type && cond.pet_type && cond.pet_type !== newPet.type) return false;
//     return cond.name.toLowerCase().includes(query);
//   });
//    return filtered.map(cond => cond.name); // return unique names
//  }, [medicalConditionObjects, medicalConditionQuery, newPet.type]);
  
  const filteredMedicalConditionOptions = React.useMemo(() => {
    const query = medicalConditionQuery.trim().toLowerCase();
    const filtered = medicalConditionObjects.filter((cond) =>
      cond.name.toLowerCase().includes(query),
    );
    return [...new Set(filtered.map((cond) => cond.name))];
  }, [medicalConditionObjects, medicalConditionQuery]);

  const toggleMedicalConditionList = () => {
    setMedicalConditionListOpen((prev) => !prev);
  };

  const addMedicalCondition = async (condition) => {
    const trimmed = String(condition || "").trim();
    if (!trimmed) return;

    const isExisting = medicalConditionOptions
      .map((o) => o.toLowerCase())
      .includes(trimmed.toLowerCase());

    if (!isExisting) {
      // New custom condition — call POST API to persist it
      try {
        const payload = { name: trimmed };
        if (newPet.type) payload.pet_type = newPet.type;
        await httpService.post("/vendorpet/addmedicalcondition", payload);
        // Refresh list from server so the new condition appears
        await fetchMedicalConditions();
        toast.success(`Medical condition "${trimmed}" added successfully.`, { position: "top-center" });
      } catch (err) {
        toast.error(`Failed to add medical condition "${trimmed}".`, { position: "top-center" });
        return;
      }
    }

    setNewPet((prev) => {
      const current = Array.isArray(prev.medical_conditions) ? prev.medical_conditions : [];
      if (current.map((c) => c.toLowerCase()).includes(trimmed.toLowerCase())) {
        // toggle off if already selected
        return {
          ...prev,
          medical_conditions: current.filter((c) => c.toLowerCase() !== trimmed.toLowerCase()),
        };
      }
      return {
        ...prev,
        medical_conditions: [...current, trimmed],
      };
    });

    setMedicalConditionQuery("");
    // Keep list open so user can continue selecting
  };

  const removeMedicalCondition = (condition) => {
    setNewPet((prev) => ({
      ...prev,
      medical_conditions: (Array.isArray(prev.medical_conditions) ? prev.medical_conditions : []).filter(
        (item) => item !== condition,
      ),
    }));
  };

  const handlePetTypeChange = (typeId) => {
    setNewPet((prev) => ({ ...prev, type: typeId, breed: "", size: "" }));
    setPetFormErrors((prev) => {
      const next = { ...prev };
      delete next.type;
      delete next.breed;
      return next;
    });
  };

  const handleBreedChange = (newValue) => {
    const breedId = newValue?._id || "";
    const selectedBreed = petBreeds.find((b) => b._id === breedId);

    let autoHairLength = "";

    if (selectedBreed?.breed_coat) {
      const coat = selectedBreed.breed_coat.toLowerCase();
      if (coat.includes("long")) {
        autoHairLength = "Long Hair";
      } else if (coat.includes("medium")) {
        autoHairLength = "Medium Hair";
      } else if (coat.includes("short")) {
        autoHairLength = "Short Hair";
      }
    }

    if (selectedBreed && selectedBreed.pet_type_id) {
      setNewPet((prev) => ({
        ...prev,
        breed: breedId,
        type: selectedBreed.pet_type_id,
        hair_length: autoHairLength || prev.hair_length,
      }));
      setPetFormErrors((prev) => {
        const next = { ...prev };
        delete next.breed;
        delete next.type;
        return next;
      });
    } else {
      setNewPet((prev) => ({
        ...prev,
        breed: breedId,
        hair_length: autoHairLength || prev.hair_length,
      }));
      setPetFormErrors((prev) => {
        const next = { ...prev };
        delete next.breed;
        return next;
      });
    }
  };

  // ---- Fetch medical conditions from API ----
 const fetchMedicalConditions = useCallback(async () => {
  try {
    const res = await httpService.get("/vendorpet/getmedicalconditions");
    const data = res?.data?.data || res?.data || [];
    const list = Array.isArray(data) ? data : [];
    setMedicalConditionObjects(list);
    // ✅ Deduplicate names
    const uniqueNames = [...new Set(list.map((item) => item.name || item).filter(Boolean))];
    setMedicalConditionOptions(uniqueNames);
  } catch (err) {
    console.warn("Failed to fetch medical conditions", err);
  }
}, []);

  // ---- Fetch vaccines from API ----
  const fetchVaccines = useCallback(async () => {
    try {
      const res = await httpService.get("/vendorpet/getvaccines");
      const data = res?.data?.data || res?.data || [];
      const list = Array.isArray(data) ? data : [];
      const apiVaccineNames = list
        .map((item) => {
          if (typeof item === "string") return item.trim();
          if (item && typeof item === "object") {
            return (
              item.name ||
              item.vaccine ||
              item.vaccine_name ||
              item.label ||
              ""
            )
              .toString()
              .trim();
          }
          return "";
        })
        .filter(Boolean);

      setVaccinationOptions((prev) => [
        ...new Set([
          ...DEFAULT_VACCINATION_OPTIONS,
          ...apiVaccineNames,
          ...prev,
        ]),
      ]);
    } catch (err) {
      console.warn("Failed to fetch vaccines", err);
    }
  }, []);

  // ---- Fetch custom pet tags from API ----
  const fetchCustomPetTags = useCallback(async () => {
    try {
      setFetchingTagsLoading(true);
      const res = await httpService.get("/vendorpet/getvendorpettags");
      const data = res?.data?.data || res?.data?.tags || res?.data || [];
      const list = Array.isArray(data) ? data : [];
      const customNames = list
        .map((item) => {
          if (typeof item === "string") return item.trim();
          if (item && typeof item === "object") {
            return (
              item.name ||
              item.tag ||
              item.label ||
              item.value ||
              ""
            )
              .toString()
              .trim();
          }
          return "";
        })
        .filter(Boolean);

      // Deduplicate case-insensitively
      const uniqueCustom = [];
      const seen = new Set();
      for (const name of customNames) {
        if (!seen.has(name.toLowerCase())) {
          seen.add(name.toLowerCase());
          uniqueCustom.push(name);
        }
      }
      setVendorCustomTags(uniqueCustom);
    } catch (err) {
      console.warn("Failed to fetch custom pet tags:", err);
    } finally {
      setFetchingTagsLoading(false);
    }
  }, []);

  // ---- Add custom pet tag API ----
  const createCustomPetTag = async (tagName) => {
    const trimmed = String(tagName || "").trim();
    if (!trimmed) {
      toast.warn("Tag name cannot be empty.", { position: "top-center" });
      return null;
    }

    // Check if already in predefined or custom tags (case-insensitive)
    const allExisting = [
      ...DEFAULT_PREDEFINED_PET_TAGS,
      ...vendorCustomTags,
    ];
    const match = allExisting.find(
      (t) => t.toLowerCase() === trimmed.toLowerCase(),
    );
    if (match) {
      return match;
    }

    try {
      setAddingTagLoading(true);
      const payload = { name: trimmed };
      await httpService.post("/vendorpet/addvendorpettags", payload);
      toast.success(`Tag "${trimmed}" added successfully.`, {
        position: "top-center",
      });
      await fetchCustomPetTags();
      return trimmed;
    } catch (err) {
      console.error("Failed to add custom tag:", err);
      toast.error(
        err?.response?.data?.message ||
          `Failed to add custom tag "${trimmed}".`,
        { position: "top-center" },
      );
      return null;
    } finally {
      setAddingTagLoading(false);
    }
  };

  const combinedPetTagOptions = React.useMemo(() => {
    const seen = new Set();
    const result = [];

    // 1. Predefined tags
    DEFAULT_PREDEFINED_PET_TAGS.forEach((tag) => {
      const lower = tag.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        result.push(tag);
      }
    });

    // 2. Vendor custom tags
    (vendorCustomTags || []).forEach((tag) => {
      const lower = tag.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        result.push(tag);
      }
    });

    // 3. Current pet tags
    (newPet.tags || []).forEach((tag) => {
      const lower = String(tag || "").trim().toLowerCase();
      if (lower && !seen.has(lower)) {
        seen.add(lower);
        result.push(tag);
      }
    });

    return result;
  }, [vendorCustomTags, newPet.tags]);

  const handlePetTagsChange = async (event, newValue) => {
    if (!Array.isArray(newValue)) return;

    // Check if user selected '+ Create "..."'
    const lastItem = newValue[newValue.length - 1];
    let tagNameToCreate = null;

    if (typeof lastItem === "string" && lastItem.startsWith("+ Create \"")) {
      const match = lastItem.match(/\+ Create "(.*)"/);
      if (match && match[1]) {
        tagNameToCreate = match[1].trim();
      }
    } else if (
      typeof lastItem === "string" &&
      !combinedPetTagOptions.some(
        (opt) => opt.toLowerCase() === lastItem.trim().toLowerCase(),
      )
    ) {
      tagNameToCreate = lastItem.trim();
    }

    if (tagNameToCreate) {
      const created = await createCustomPetTag(tagNameToCreate);
      const tagToUse = created || tagNameToCreate;
      const baseList = newValue.filter(
        (t) => typeof t === "string" && !t.startsWith("+ Create \""),
      );
      const updated = Array.from(new Set([...baseList, tagToUse]));
      handleFieldChange("tags", updated);
      setTagSearchQuery("");
      return;
    }

    const baseList = newValue.filter(
      (t) => typeof t === "string" && !t.startsWith("+ Create \""),
    );
    const updated = Array.from(new Set(baseList));
    handleFieldChange("tags", updated);
    setTagSearchQuery("");
  };

  // ---- Fetch initial data ----
  useEffect(() => {
    if (isOpen) {
      const fetchInitialData = async () => {
        setLoading(true);
        try {
          const [vendorsRes, typesRes, breedsRes, sizesRes] = await Promise.all(
            [
              httpService.get("/vendors/getPetTypes?isEnabled=true"),
              httpService.get("/petTypes/getPetTypes"),
              httpService.get("/petBreeds/getPetBreeds"),
              httpService.get("/vendorpetsize/getVendorPetSizes"),
            ],
          );

          const vendorsData = vendorsRes.data || [];
          const enabledPetTypeIds = new Set(
            vendorsData
              .filter((vendorPet) => vendorPet.enabled === true)
              .map((vendorPet) => vendorPet.pet_type_id),
          );

          const allPetTypes = typesRes.data || [];
          const mappedPetTypes = allPetTypes
            .filter((type) => enabledPetTypeIds.has(type._id))
            .map((type) => ({
              _id: type._id,
              name: type.name,
              pet_type_id: type._id,
              enabled: true,
            }));

          setPetTypes(mappedPetTypes);

          const breedsData = breedsRes.data || [];
          setPetBreeds(breedsData);

          const allSizes = sizesRes.data || [];
          setAllPetSizes(allSizes);

          const order = ["Small", "Medium", "Large", "Giant"];
          const uniqueSizes = [];
          const seen = new Set();
          for (const size of allSizes) {
            if (!seen.has(size.size_label)) {
              seen.add(size.size_label);
              uniqueSizes.push(size);
            }
          }
          const sorted = uniqueSizes.sort(
            (a, b) => order.indexOf(a.size_label) - order.indexOf(b.size_label),
          );
          setPetSizes(sorted);
        } catch (error) {
          console.error("Failed to load initial data", error);
          toast.error("Failed to load pet data", { position: "top-center" });
        } finally {
          setLoading(false);
        }
      };

      fetchInitialData();
      fetchMedicalConditions();
      fetchVaccines();
      fetchCustomPetTags();
    }
  }, [isOpen, fetchMedicalConditions, fetchVaccines, fetchCustomPetTags]);

  // ---- Pet name uniqueness (unchanged) ----
  const getUniquePetName = (
    desiredName,
    existingPets,
    petIdToExclude = null,
  ) => {
    if (!desiredName) return desiredName;

    const trimmedName = desiredName.trim();

    const isDuplicate = existingPets.some(
      (pet) =>
        pet.name?.trim().toLowerCase() === trimmedName.toLowerCase() &&
        (petIdToExclude ? pet._id !== petIdToExclude : true),
    );

    if (!isDuplicate) return trimmedName;

    let maxNumber = 0;
    const baseName = trimmedName;

    existingPets.forEach((pet) => {
      const petName = pet.name?.trim() || "";
      const match = petName.match(/^(\d+)-/);
      if (match && petName.toLowerCase().endsWith(baseName.toLowerCase())) {
        const num = parseInt(match[1], 10);
        if (num > maxNumber) maxNumber = num;
      }
    });

    return `${maxNumber + 1}-${baseName}`;
  };

  const markSetupStepComplete = async (stepKey) => {
     if (!vendorId || !stepKey) {
       console.log("Exiting early — vendorId missing:", vendorId); // TEMP
       return;
     }
    if (!vendorId || !stepKey) return;
    try {
      await httpService.put(
        `/vendorregistration/updatesetup/${vendorId}`,
        {
          steps: [
            {
              key: stepKey,
              completed: true,
            },
          ],
        },
      );
    } catch (err) {
      console.warn("Failed to update setup step: client_pet", err);
    }
  };

  // ---- File upload for vaccine documents ----
  const uploadSingleFile = async (file) => {
    const formData = new FormData();
    formData.append("module", "vendors");
    formData.append("docType", "vaccineRecord");
    formData.append("vendorId", vendorId);
    formData.append("file", file);

    try {
      const res = await httpService.post("/upload/fileUpload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const s3Key =
        res?.data?.data?.s3Key || res?.data?.s3Key || res?.data?.data?.key;
      const s3Url =
        res?.data?.data?.s3Url || res?.data?.data?.url || `${S3_BASE}/${s3Key}`;

      return { name: file.name, url: s3Url };
    } catch (err) {
      toast.error("File upload failed");
      return null;
    }
  };

  const handleVaccineFileUpload = async (files) => {
    const uploadedDocs = [];
    for (const file of files) {
      const uploaded = await uploadSingleFile(file);
      if (uploaded) uploadedDocs.push(uploaded);
    }
    if (uploadedDocs.length > 0) {
      setCurrentVaccineRecord((prev) => ({
        ...prev,
        documents: [...prev.documents, ...uploadedDocs],
      }));
    }
  };

  // ---- Vaccination drawer handlers ----
  const openVaccineDrawer = (vaccineName) => {
    setVaccineDrawerMode("record");
    setNewMasterVaccineName("");
    const selectedVaccine =
      typeof vaccineName === "string" ? vaccineName : "";
    setCurrentVaccineRecord({
      id: `temp-${Date.now()}`,
      vaccine: selectedVaccine,
      date_given: "",
      next_due: "",
      documents: [],
    });
    setVaccineSearchQuery(selectedVaccine || "");
    setVaccineDropdownOpen(false);
    setShowVaccineDrawer(true);
  };

  const openCreateVaccineDrawer = () => {
    setVaccineDrawerMode("createMaster");
    setNewMasterVaccineName("");
    setCurrentVaccineRecord({
      id: `temp-${Date.now()}`,
      vaccine: "",
      date_given: "",
      next_due: "",
      documents: [],
    });
    setVaccineSearchQuery("");
    setVaccineDropdownOpen(false);
    setShowVaccineDrawer(true);
  };

  const closeVaccineDrawer = () => {
    setShowVaccineDrawer(false);
    setCurrentVaccineRecord(null);
    setIsDraggingVaccineFile(false);
    setVaccineSearchQuery("");
    setVaccineDropdownOpen(false);
    setVaccineDrawerMode("record");
    setNewMasterVaccineName("");
  };

  const handleAddMasterVaccine = async () => {
    const trimmed = String(newMasterVaccineName || "").trim();
    if (!trimmed) {
      toast.error("Please enter a vaccine name.", { position: "top-center" });
      return;
    }

    const existing = vaccinationOptions.find(
      (opt) => opt.toLowerCase() === trimmed.toLowerCase(),
    );

    if (existing) {
      toast.error(`Vaccine "${existing}" already exists.`, {
        position: "top-center",
      });
      return;
    }

    try {
      setAddingVaccineLoading(true);
      const payload = { name: trimmed };
      await httpService.post("/vendorpet/addvaccinemaster", payload);
      toast.success(`Vaccine "${trimmed}" added successfully.`, {
        position: "top-center",
      });
      await fetchVaccines();
      setVaccinationOptions((prev) =>
        prev.some((o) => o.toLowerCase() === trimmed.toLowerCase())
          ? prev
          : [...prev, trimmed],
      );
      closeVaccineDrawer();
    } catch (err) {
      console.error("Failed to add vaccine:", err);
      toast.error(
        err?.response?.data?.message || `Failed to add vaccine "${trimmed}".`,
        { position: "top-center" },
      );
    } finally {
      setAddingVaccineLoading(false);
    }
  };

  const handleCreateCustomVaccine = async (customName) => {
    const trimmed = String(customName || "").trim();
    if (!trimmed) return;

    const existing = vaccinationOptions.find(
      (opt) => opt.toLowerCase() === trimmed.toLowerCase(),
    );

    if (existing) {
      handleVaccineInput("vaccine", existing);
      setVaccineSearchQuery(existing);
      setVaccineDropdownOpen(false);
      return;
    }

    try {
      setAddingVaccineLoading(true);
      const payload = { name: trimmed };
      await httpService.post("/vendorpet/addvaccinemaster", payload);
      toast.success(`Vaccine "${trimmed}" added successfully.`, {
        position: "top-center",
      });
      await fetchVaccines();
      setVaccinationOptions((prev) =>
        prev.some((o) => o.toLowerCase() === trimmed.toLowerCase())
          ? prev
          : [...prev, trimmed],
      );
      handleVaccineInput("vaccine", trimmed);
      setVaccineSearchQuery(trimmed);
      setVaccineDropdownOpen(false);
    } catch (err) {
      console.error("Failed to add vaccine:", err);
      toast.error(
        err?.response?.data?.message || `Failed to add vaccine "${trimmed}".`,
        { position: "top-center" },
      );
    } finally {
      setAddingVaccineLoading(false);
    }
  };

  const handleVaccineInput = (field, value) => {
    setCurrentVaccineRecord((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "date_given" && value) {
        updated.next_due = addMonths(value, 11);
      }
      return updated;
    });
  };

  const saveVaccineRecord = () => {
    if (!currentVaccineRecord) return;
    if (
      !currentVaccineRecord.vaccine ||
      currentVaccineRecord.vaccine === "Select Vaccine"
    ) {
      toast.error("Please select or enter a vaccine name");
      return;
    }
    // date_given is optional

    // Add to vaccination_records
    const newRecord = {
      vaccine: currentVaccineRecord.vaccine,
      date_given: currentVaccineRecord.date_given,
      next_due: currentVaccineRecord.next_due,
      documents: currentVaccineRecord.documents,
    };
    setNewPet((prev) => ({
      ...prev,
      vaccination_records: [...(prev.vaccination_records || []), newRecord],
    }));
    setVaccinationOptions((prev) =>
      prev.includes(newRecord.vaccine) ? prev : [...prev, newRecord.vaccine],
    );
    closeVaccineDrawer();
  };

  // ---- Save pet ----
  const handleAddNewPet = async (petData, shouldClose = true) => {
    const matchingSize = allPetSizes.find(
      (petSize) => petSize._id === petData.size,
    );

    const currentExistingPets = existingPets || [];

    const isNameExistsWithSuffix = (baseName, excludeId = null) => {
      return currentExistingPets.some((pet) => {
        if (!pet?.name) return false;
        if (excludeId && pet._id === excludeId) return false;
        const petName = pet.name?.trim().toLowerCase() || "";
        const baseNameLower = baseName.trim().toLowerCase();
        if (petName === baseNameLower) return true;
        const pattern = new RegExp(`^${escapeRegex(baseNameLower)}-(\\d+)$`);
        if (pattern.test(petName)) return true;
        return false;
      });
    };

    const escapeRegex = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };

    const getNextSuffixNumber = (baseName, excludeId = null) => {
      let maxNumber = 0;
      currentExistingPets.forEach((pet) => {
        if (!pet?.name) return;
        if (excludeId && pet._id === excludeId) return;
        const petName = pet.name?.trim() || "";
        const baseNameLower = baseName.trim().toLowerCase();
        if (petName.toLowerCase() === baseNameLower) {
          maxNumber = Math.max(maxNumber, 0);
        }
        const pattern = new RegExp(
          `^${escapeRegex(baseNameLower)}-(\\d+)$`,
          "i",
        );
        const match = petName.match(pattern);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxNumber) maxNumber = num;
        }
      });
      return maxNumber + 1;
    };

    let finalPetName = petData.name;

    if (!petId) {
      if (isNameExistsWithSuffix(petData.name)) {
        const nextNumber = getNextSuffixNumber(petData.name);
        finalPetName = `${petData.name.trim()}-${nextNumber}`;
        toast.info(
          `Pet name "${petData.name}" already exists. Saving as "${finalPetName}"`,
          {
            position: "top-center",
            autoClose: 3000,
          },
        );
      }
    } else {
      const originalPet = originalPetDataRef.current;
      const nameChanged = originalPet?.name !== petData.name;
      if (nameChanged && isNameExistsWithSuffix(petData.name, petId)) {
        const nextNumber = getNextSuffixNumber(petData.name, petId);
        finalPetName = `${petData.name.trim()}-${nextNumber}`;
        toast.info(
          `Pet name "${petData.name}" already exists. Saving as "${finalPetName}"`,
          {
            position: "top-center",
            autoClose: 3000,
          },
        );
      }
    }

    try {
      setLoading(true);

      const selectedVendorSize = allPetSizes.find(
        (size) => size._id === petData.size,
      );

      const flps_pet_size_id = selectedVendorSize?.flps_pet_size_id || null;

      // Build vaccination_records payload
      const vaccinationRecords = petData.vaccination_records.map((rec) => ({
        vaccine: rec.vaccine,
        date_given: rec.date_given || null,
        next_due: rec.next_due || null,
        documents: rec.documents || [],
      }));

      const normalizedVaccinations = vaccinationRecords.map((r) => r.vaccine);

      const petPayload = {
        name: finalPetName,
        pet_type: petData.type,
        gender: petData.gender,
        size: flps_pet_size_id,
        vendor_pet_size_id: petData.size,
        breed: petData.breed || null,
        weight_kg: petData.weight_kg || null,
        age: petData.age || null,
        birthday: petData.birthday || null,
        behavior_notes: petData.behavior_notes,
        tags: normalizeStringArray(petData.tags),
        medical_condition: Array.from(
          new Set(normalizeStringArray(petData.medical_conditions)),
        ),
        vaccinations: normalizedVaccinations,
        vaccination_records: vaccinationRecords, // NEW
        hair_type: petData.hair_type || "",
        hair_length: petData.hair_length || "",
        color: normalizeStringArray(petData.color),
        meals_per_day: petData.meals_per_day || "",
        walks_per_day: petData.walks_per_day || "",
        meal_type: normalizeStringArray(petData.meal_type),
        neutered_status: petData.neutered_status || "",
        reference_source: petData.reference || "",
        reference: petData.reference || "",
        source: petData.reference || "",
        vendor_client_id: clientId,
        ongoing_medications:
          newPet.ongoing_medications ||
          originalPetDataRef.current?.ongoing_medications ||
          [],
        meals: (newPet.meals || originalPetDataRef.current?.meals || []).map((m) => ({
          name: m.name || "",
          time: m.time || "",
          quantity: m.quantity || "",
          notes: m.notes || "",
          added_date: m.added_date || new Date().toISOString(),
        })),
        meal_history:
          newPet.meal_history ||
          originalPetDataRef.current?.meal_history ||
          [],
        custom_fields: petData.custom_fields || newPet.custom_fields || {},
      };

      if (isImageChanged) {
        if (uploadedImageUrl || profileImagePreview?.startsWith("http")) {
          petPayload.image = uploadedImageUrl || profileImagePreview;
        } else if (imageFile) {
          const uploadedImageUrlValue = await uploadFileToS3(imageFile, {
            module: "client-pets",
            docType: "profile",
            returnType: "fullUrl",
          });
          petPayload.image = uploadedImageUrlValue;
          setUploadedImageUrl(uploadedImageUrlValue);
        } else {
          petPayload.image = "";
        }
      }

      let response;

      if (petId) {
        response = await httpService.put(
          `/vendorpet/updatevendorpet/${petId}`,
          petPayload,
        );
        toast.success("Pet updated successfully!", {
          position: "top-center",
        });

        if (onEditSave) {
          onEditSave(originalPetDataRef.current, {
            ...petPayload,
            petSizeLabel: matchingSize?.size_label,
          });
        }
      } else {
        response = await httpService.post(
          "/vendorpet/addvendorpet",
          petPayload,
        );
        toast.success("Pet added successfully!", {
          position: "top-center",
        });
      }

      if (response) {
        const responseData = response?.data?.data || response?.data || {};
        const normalizedSavedPet = {
          ...petPayload,
          ...responseData,
          vaccinations: extractVaccinationNames(responseData),
          _id: petId || responseData?._id || responseData?.id,
        };

        if (Array.isArray(responseData.meal_history)) {
          setNewPet((prev) => ({
            ...prev,
            meal_history: responseData.meal_history,
            meals: Array.isArray(responseData.meals)
              ? responseData.meals
              : prev.meals,
          }));
        }

        const isFirstPetForClient =
          !petId && (existingPets?.length || 0) === 0;
        if (isFirstPetForClient) {
          markSetupStepComplete("client_pet");
        }

        if (shouldClose) {
          onSave?.(normalizedSavedPet);
          setTimeout(() => onClose(), 800);
        } else {
          onSaveAndContinue?.(normalizedSavedPet);
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error("Error saving pet:", error);
      toast.error(`Failed to ${petId ? "update" : "add"} pet`, {
        position: "top-center",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleNewPetSave = async () => {
    const errors = validateForm(filteredPetSizes);
    setPetFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.warn("Please fill all required fields", { position: "top-center" });
      return false;
    }
    return await handleAddNewPet(newPet, true);
  };

  const resetForm = () => {
    setNewPet({
      type: "",
      name: "",
      gender: "",
      size: "",
      breed: "",
      weight_kg: "",
      age: "",
      birthday: "",
      behavior_notes: "",
      tags: [],
      medical_conditions: [],
      vaccinations: [],
      vaccination_records: [],
      hair_type: "",
      hair_length: "",
      color: [],
      meals_per_day: "",
      walks_per_day: "",
      meal_type: [],
      neutered_status: "",
      reference: "",
      ongoing_medications: [],
      meals: [],
      meal_history: [],
      custom_fields: {},
    });
    setAgeYears("");
    setAgeMonths("");
    setHealthTab("vaccination");
    setMedicalConditionQuery("");
    setMedicalConditionListOpen(true);
    setCurrentVaccineRecord(null);
    setShowVaccineDrawer(false);
    setVaccineSearchQuery("");
    setVaccineDropdownOpen(false);
    setFileViewUrl(null);
    setPetFormErrors({});
    originalPetDataRef.current = null;
    setFormReady(true);
    setFormVisible(true);
    setProfileImagePreview("");
    setUploadedImageUrl("");
    setImageFile(null);
    setIsImageChanged(false);
    document.getElementById("pet-image-input") &&
      (document.getElementById("pet-image-input").value = "");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleBackClick = () => {
    if (onBack) onBack();
    else handleClose();
  };

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    }
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // ---- Initialize Drawer ----
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const waitForNextPaint = () =>
      new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      );

    const initializeDrawer = async () => {
      if (!petId) {
        resetForm();
        hydratedPetIdRef.current = null;
        setFormReady(true);
        setFormVisible(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setFormReady(false);
      setFormVisible(true);

      try {
        const [vendorsRes, typesRes, breedsRes, sizesRes] = await Promise.all([
          httpService.get("/vendors/getPetTypes?isEnabled=true"),
          httpService.get("/petTypes/getPetTypes"),
          httpService.get("/petBreeds/getPetBreeds"),
          httpService.get("/vendorpetsize/getVendorPetSizes"),
        ]);

        if (cancelled) return;

        const vendorsData = vendorsRes.data || [];
        const enabledPetTypeIds = new Set(
          vendorsData
            .filter((vendorPet) => vendorPet.enabled === true)
            .map((vendorPet) => vendorPet.pet_type_id),
        );

        const allPetTypes = typesRes.data || [];
        const mappedPetTypes = allPetTypes
          .filter((type) => enabledPetTypeIds.has(type._id))
          .map((type) => ({
            _id: type._id,
            name: type.name,
            pet_type_id: type._id,
            enabled: true,
          }));
        setPetTypes(mappedPetTypes);

        const breedsData = breedsRes.data || [];
        setPetBreeds(breedsData);

        const allSizes = sizesRes.data || [];
        setAllPetSizes(allSizes);

        if (hydratedPetIdRef.current !== petId) {
          await getPetDetails(allSizes);
          await waitForNextPaint();
        }

        if (!cancelled) {
          setFormVisible(true);
          setFormReady(true);
        }
      } catch (error) {
        console.error("Failed to load initial data", error);
        toast.error("Failed to load pet data", { position: "top-center" });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    initializeDrawer();

    return () => {
      cancelled = true;
    };
  }, [isOpen, petId, getPetDetails]);

  if (!isOpen) return null;

  const isEditHydrating = !!petId && loading;
  const showDrawerLoader = isEditHydrating || !formReady;

  return (
    <div
      className={`fixed inset-0 z-50 transition-all duration-300 ease-in-out ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ overflow: "hidden" }}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]"
        onClick={handleClose}
      />
      <div
        className={`absolute right-0 top-0 h-full w-full ${!isFullWidth && "md:w-[60vw] lg:w-[60vw] xl:w-1/2"} bg-white shadow-xl transform transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
        style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {/* Header */}
        <div
          className="flex items-center p-4 bg-indigo-50 border-b border-indigo-200"
          style={{ position: "sticky", top: 0, zIndex: 1000, flexShrink: 0 }}
        >
          <button
            onClick={handleBackClick}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-indigo-100 transition-colors duration-200 mr-3"
          >
            <HiArrowSmLeft className="w-5 h-5 text-[#4b5563]" />
          </button>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <span className="text-primary text-xl">
                <PetsIcon />
              </span>
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              {!petId ? "Add New Pet" : "Update Pet"}
            </h2>
          </div>

          <div className="flex items-start gap-3 ml-auto">
            <div className="relative">
              {profileImagePreview ? (
                <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-indigo-200">
                  <img
                    src={profileImagePreview}
                    alt="Pet"
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => document.getElementById("pet-image-input").click()}
                  />
                  <button
                    onClick={removeImage}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="pet-image-input"
                  className="w-16 h-16 rounded-full border-2 border-dashed border-gray-300 hover:border-indigo-400 flex items-center justify-center cursor-pointer transition"
                >
                  <AddPhotoAlternateIcon className="text-gray-400 text-2xl" />
                </label>
              )}
              <input
                id="pet-image-input"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          className="flex flex-col relative"
          style={{ flex: 1, height: 0, overflow: "hidden" }}
        >
          <div
            className={`absolute inset-0 z-30 flex items-center justify-center bg-transparent transition-all duration-300 ease-in-out ${
              showDrawerLoader
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none"
            }`}
          >
            <LoaderSpiner text="Loading . . ." />
          </div>

          <div
            className={`flex-1 overflow-y-auto overflow-x-hidden px-4 py-2 relative pb-24 transition-all duration-300 ease-in-out ${
              formVisible
                ? showDrawerLoader
                  ? "opacity-80"
                  : "opacity-100"
                : "opacity-0"
            } ${showDrawerLoader ? "pointer-events-none" : ""}`}
            style={{ paddingBottom: "80px" }}
          >
            {/* Row 1: Pet Name, Type, Breed */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="flex items-center gap-2 mb-1 text-base font-semibold">
                  <span className="block text-sm font-semibold mb-1">
                    {formConfig.labelMap["pet.name"] || "Pet Name"}{" "}
                  </span>
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPet.name}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                  placeholder={`Enter ${(formConfig.labelMap["pet.name"] || "Pet Name")}`}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    petFormErrors.name ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {petFormErrors.name && (
                  <p className="text-xs text-red-500 mt-1">
                    {petFormErrors.name}
                  </p>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 mb-1 text-base font-semibold">
                  <span className="block text-sm font-semibold mb-1">
                    {formConfig.labelMap["pet.pet_type"] ||
                      formConfig.labelMap["pet.type"] ||
                      "Type"}
                  </span>
                  <span className="text-red-500">*</span>
                </label>
                <select
                  value={newPet.type || ""}
                  onChange={(e) => handlePetTypeChange(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    petFormErrors.type ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="" disabled>
                    Select Pet Type
                  </option>
                  {petTypes.map((type) => (
                    <option key={type._id} value={type._id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {petFormErrors.type && (
                  <p className="text-xs text-red-500 mt-1">
                    {petFormErrors.type}
                  </p>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 mb-1 text-base font-semibold">
                  <label className="block text-sm font-semibold mb-1">
                    {formConfig.labelMap["pet.breed"] || "Breed"}
                  </label>
                  {isDogOrCat && !newPet.breed && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <Autocomplete
                  options={filteredBreeds}
                  value={petBreeds.find((b) => b._id === newPet.breed) || null}
                  onChange={(e, v) => handleBreedChange(v)}
                  getOptionLabel={(opt) => opt?.breed_name || ""}
                  isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={
                        !newPet.type
                          ? "Select pet type first"
                          : filteredBreeds.length === 0
                            ? `No breeds found for ${
                                petTypes.find((t) => t._id === newPet.type)
                                  ?.name || "this type"
                              }`
                            : "Select breed"
                      }
                      fullWidth
                      error={!!petFormErrors.breed}
                      helperText={petFormErrors.breed}
                    />
                  )}
                  disabled={!newPet.type || filteredBreeds.length === 0}
                />
              </div>
            </div>

            {/* Row 2: Size, Gender, Weight */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {formConfig.labelMap["pet.size"] || "Size"}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  value={newPet.size || ""}
                  onChange={(e) => handleFieldChange("size", e.target.value)}
                  disabled={!newPet.type || filteredPetSizes.length === 0}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    petFormErrors.size ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="" disabled>
                    {!newPet.type
                      ? "Select pet type first"
                      : filteredPetSizes.length === 0
                        ? "No sizes available for this pet type"
                        : "Select Pet Size"}
                  </option>
                  {filteredPetSizes.map((size) => (
                    <option key={size._id} value={size._id}>
                      {size.size_label}
                    </option>
                  ))}
                </select>
                {petFormErrors.size && (
                  <p className="text-xs text-red-500 mt-1">
                    {petFormErrors.size}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {formConfig.labelMap["pet.gender"] || "Gender"}
                </label>
                <select
                  value={newPet.gender || ""}
                  onChange={(e) => handleFieldChange("gender", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select Gender</option>
                  <option>Male</option>
                  <option>Female</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {formConfig.labelMap["pet.weight_kg"] || "Weight (kg)"}
                </label>
                <input
                  type="text"
                  value={newPet.weight_kg || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,3}(\.\d{0,2})?$/.test(value)) {
                      handleFieldChange("weight_kg", value);
                    }
                  }}
                  placeholder="e.g. 5.2"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 ${
                    newPet.weight_kg &&
                    !/^\d{1,3}(\.\d{0,2})?$/.test(newPet.weight_kg)
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {newPet.weight_kg &&
                  !/^\d{1,3}(\.\d{0,2})?$/.test(newPet.weight_kg) && (
                    <p className="text-xs text-red-500 mt-1">
                      Weight can be up to 3 digits (e.g., 999 or 45.5).
                    </p>
                  )}
              </div>
            </div>

            {/* Row 3: Birth Date + Neutered Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {formConfig.labelMap["pet.birthday"] ||
                    formConfig.labelMap["pet.age"] ||
                    "Birth Date / Age"}
                </label>

                <div className="flex items-center gap-4 bg-gray-50 p-3 rounded-xl border">
                  <div className="flex bg-white rounded-lg shadow-sm overflow-hidden shrink-0">
                    <button
                      type="button"
                      onClick={() => setAgeMode("dob")}
                      className={`px-3 py-1 text-sm font-semibold whitespace-nowrap transition ${
                        ageMode === "dob"
                          ? "bg-indigo-600 text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      Select DOB
                    </button>
                    <button
                      type="button"
                      onClick={() => setAgeMode("age")}
                      className={`px-3 py-1 text-sm font-semibold whitespace-nowrap transition ${
                        ageMode === "age"
                          ? "bg-indigo-600 text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      Enter Age
                    </button>
                  </div>

                  <div className="flex-1">
                    {ageMode === "dob" ? (
                      <input
                        type="date"
                        value={newPet.birthday || ""}
                        onChange={handleBirthDateChange}
                        max={new Date().toISOString().split("T")[0]}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={ageYears}
                          onChange={handleAgeYearsChange}
                          className="px-3 py-1 border rounded-lg text-sm focus:ring-indigo-500"
                        >
                          <option value="">Years</option>
                          {Array.from({ length: 15 }, (_, i) => (
                            <option key={i} value={i}>
                              {i}
                            </option>
                          ))}
                        </select>

                        <select
                          value={ageMonths}
                          onChange={handleAgeMonthsChange}
                          className="px-3 py-1 border rounded-lg text-sm focus:ring-indigo-500"
                        >
                          <option value="">Months</option>
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i} value={i}>
                              {i}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {newPet.age && (
                  <p className="mt-2 text-sm font-medium text-indigo-600">
                    Age: {newPet.age}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  {formConfig.labelMap["pet.neutered_status"] ||
                    "Neutered Status"}
                </label>
                <select
                  value={newPet.neutered_status || ""}
                  onChange={(e) =>
                    handleFieldChange("neutered_status", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Select Neutered Status</option>
                  <option value="Not Neutered">Not Neutered</option>
                  <option value="Neutered">Neutered</option>
                  <option value="Unknown">Unknown</option>
                </select>
              </div>
            </div>

            {/* Row 4: Pet Tags */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-semibold">
                  Pet Tags
                </label>
                {fetchingTagsLoading && (
                  <span className="text-xs text-gray-400">Loading custom tags...</span>
                )}
              </div>
              <Autocomplete
                multiple
                freeSolo
                options={combinedPetTagOptions}
                value={newPet.tags || []}
                inputValue={tagSearchQuery}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setTagSearchQuery(newInputValue);
                  } else if (reason === "reset" || reason === "clear") {
                    setTagSearchQuery("");
                  }
                }}
                filterOptions={(options, params) => {
                  const input = (params.inputValue || "").trim().toLowerCase();
                  const filtered = options.filter((opt) =>
                    opt.toLowerCase().includes(input),
                  );
                  const hasExact = options.some(
                    (opt) => opt.toLowerCase() === input,
                  );
                  if (params.inputValue.trim() && !hasExact) {
                    filtered.push(`+ Create "${params.inputValue.trim()}"`);
                  }
                  return filtered;
                }}
                renderOption={(props, option) => {
                  const isCreate =
                    typeof option === "string" &&
                    option.startsWith("+ Create \"");
                  if (isCreate) {
                    return (
                      <li
                        {...props}
                        key="create-option"
                        className="px-4 py-2.5 text-sm text-indigo-600 font-semibold hover:bg-indigo-50 flex items-center gap-2 cursor-pointer border-t border-gray-100"
                      >
                        <Plus size={16} className="text-indigo-600 flex-shrink-0" />
                        <span>{option}</span>
                      </li>
                    );
                  }
                  const isSelected = (newPet.tags || []).some(
                    (t) => t.toLowerCase() === option.toLowerCase(),
                  );
                  return (
                    <li
                      {...props}
                      key={option}
                      className={`px-4 py-2 text-sm flex items-center justify-between cursor-pointer ${
                        isSelected
                          ? "bg-indigo-50 text-indigo-700 font-semibold"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <span>{option}</span>
                      {isSelected && (
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-600 text-white text-[10px]">
                          ✓
                        </span>
                      )}
                    </li>
                  );
                }}
                onChange={handlePetTagsChange}
                renderTags={(v, getTagProps) =>
                  v.map((opt, i) => (
                    <Chip
                      variant="outlined"
                      label={opt}
                      {...getTagProps({ index: i })}
                      key={i}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#f3f4f6",
                        borderColor: "#d1d5db",
                        fontWeight: 500,
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="medium"
                    placeholder="Select or search tags, or type to create..."
                    onKeyDown={async (e) => {
                      if (e.key === "Enter" && tagSearchQuery.trim()) {
                        const trimmed = tagSearchQuery.trim();
                        const isExisting = combinedPetTagOptions.some(
                          (opt) => opt.toLowerCase() === trimmed.toLowerCase(),
                        );
                        if (!isExisting) {
                          e.preventDefault();
                          e.stopPropagation();
                          const created = await createCustomPetTag(trimmed);
                          const tagToUse = created || trimmed;
                          const current = newPet.tags || [];
                          if (
                            !current.some(
                              (t) => t.toLowerCase() === tagToUse.toLowerCase(),
                            )
                          ) {
                            handleFieldChange("tags", [...current, tagToUse]);
                          }
                          setTagSearchQuery("");
                        }
                      }
                    }}
                    sx={{
                      mb: 1.5,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                      },
                    }}
                  />
                )}
              />
            </div>

            {/* Dynamic Pet Custom Fields (e.g. Microchip Number, Favorite Toy, etc.) */}
            {formConfig.petCustomFields.length > 0 && (
              <div className="mt-4 pt-4 border-t border-dashed border-gray-200">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Additional Pet Basic Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {formConfig.petCustomFields.map((field) => {
                    const value = getPetCustomFieldValue(field);
                    const aType = String(
                      field.answerType || "text",
                    ).toLowerCase();

                    if (
                      aType === "dropdown" ||
                      aType === "select" ||
                      aType === "mcq" ||
                      aType === "single_choice"
                    ) {
                      return (
                        <div key={field.field_uid || field._id}>
                          <label className="block text-sm font-semibold mb-1">
                            {field.label}
                            {field.isRequired && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </label>
                          <select
                            value={value}
                            onChange={(e) =>
                              handlePetCustomFieldChange(field, e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">Select an option</option>
                            {(field.options || []).map((opt, idx) => {
                              const text =
                                typeof opt === "object"
                                  ? opt.optionText || opt.label
                                  : opt;
                              return (
                                <option key={idx} value={text}>
                                  {text}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      );
                    }

                    if (aType === "textarea" || aType === "paragraph") {
                      return (
                        <div
                          key={field.field_uid || field._id}
                          className="col-span-1 md:col-span-3"
                        >
                          <label className="block text-sm font-semibold mb-1">
                            {field.label}
                            {field.isRequired && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </label>
                          <textarea
                            rows={3}
                            value={value}
                            onChange={(e) =>
                              handlePetCustomFieldChange(field, e.target.value)
                            }
                            placeholder={
                              field.placeholder ||
                              `Enter ${field.label.toLowerCase()}`
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      );
                    }

                    if (aType === "date") {
                      return (
                        <div key={field.field_uid || field._id}>
                          <label className="block text-sm font-semibold mb-1">
                            {field.label}
                            {field.isRequired && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </label>
                          <input
                            type="date"
                            value={value}
                            onChange={(e) =>
                              handlePetCustomFieldChange(field, e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      );
                    }

                    return (
                      <div key={field.field_uid || field._id}>
                        <label className="block text-sm font-semibold mb-1">
                          {field.label}
                          {field.isRequired && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                        </label>
                        <input
                          type={
                            aType === "number"
                              ? "number"
                              : aType === "phone"
                                ? "tel"
                                : "text"
                          }
                          value={value}
                          onChange={(e) =>
                            handlePetCustomFieldChange(field, e.target.value)
                          }
                          placeholder={
                            field.placeholder ||
                            `Enter ${field.label.toLowerCase()}`
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Leftover Saved Pet Custom Fields */}
            {/* {Object.keys(newPet.custom_fields || {}).filter(
              (k) =>
                !isCustomerScopeField(k, k, k) &&
                !isNativePetField(k) &&
                !formConfig.petCustomFields.some(
                  (f) =>
                    String(f.field_uid) === String(k) ||
                    String(f._id) === String(k) ||
                    String(f.label) === String(k),
                ) &&
                newPet.custom_fields[k] !== undefined &&
                newPet.custom_fields[k] !== null &&
                newPet.custom_fields[k] !== "",
            ).length > 0 && (
              <div className="mt-4 pt-4 border-t border-dashed border-gray-200">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Other Saved Pet Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {Object.entries(newPet.custom_fields).map(([k, v]) => {
                    if (
                      v === undefined ||
                      v === null ||
                      v === "" ||
                      isCustomerScopeField(k, k, k) ||
                      isNativePetField(k)
                    )
                      return null;
                    const label = k
                      .replace(/^pet\./, "")
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase());
                    return (
                      <div key={k}>
                        <label className="block text-sm font-semibold mb-1">
                          {label}
                        </label>
                        <input
                          type="text"
                          value={
                            typeof v === "object" ? JSON.stringify(v) : String(v)
                          }
                          onChange={(e) =>
                            setNewPet((prev) => ({
                              ...prev,
                              custom_fields: {
                                ...(prev.custom_fields || {}),
                                [k]: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )} */}

            {/* Row 5: Health Tabs */}
            <div className="mt-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 border-b pb-3 w-full">
                {[
                  { id: "vaccination", label: "Vaccination" },
                  { id: "medical", label: "Medical Condition" },
                  { id: "medication", label: "Medication" },
                  { id: "appearance", label: "Appearance" },
                  { id: "diet", label: "Diet" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setHealthTab(tab.id)}
                    className={`w-full rounded-none px-4 py-2 text-sm font-semibold border ${
                      healthTab === tab.id
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-gray-700 border-gray-300"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* ============= VACCINATION TAB (NEW UI) ============= */}
              {healthTab === "vaccination" && (
                <div className="mt-4">
                  {/* Table */}
                  <div className="bg-white rounded-xl shadow border overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full border-separate border-spacing-0">
                        <thead className="bg-gray-50 text-gray-700 text-sm uppercase tracking-wide">
                          <tr>
                            <th className="px-6 py-4 font-semibold text-left border-b border-gray-200">
                              Vaccine
                            </th>
                            <th className="px-6 py-4 font-semibold text-left border-b border-gray-200">
                              Given On
                            </th>
                            <th className="px-6 py-4 font-semibold text-left border-b border-gray-200">
                              Next Due
                            </th>
                            <th className="px-6 py-4 font-semibold text-left border-b border-gray-200">
                              Upload File
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {vaccinationOptions.map((name, idx) => {
                            // Find all records matching this vaccine name (handling DHPP/DHPPi aliases and case-insensitivity)
                            const normName = String(name || "").trim().toLowerCase();
                            const records = (newPet.vaccination_records || []).filter((r) => {
                              const rName = String(r?.vaccine || "").trim().toLowerCase();
                              if (rName === normName) return true;
                              if (
                                (normName === "dhpp" || normName === "dhppi") &&
                                (rName === "dhpp" || rName === "dhppi")
                              )
                                return true;
                              return false;
                            });

                            // Extract valid records with actual dates
                            const validRecords = records
                              .map((r) => {
                                const rawDate =
                                  r?.date_given ||
                                  r?.date ||
                                  r?.given_date ||
                                  r?.vaccination_date ||
                                  r?.administered_date ||
                                  r?.createdAt;
                                const parsedTime = parseDateToTime(rawDate);
                                return {
                                  ...r,
                                  rawDate,
                                  parsedTime,
                                };
                              })
                              .filter((r) => r.parsedTime !== null && !isNaN(r.parsedTime));

                            // Sort descending by actual recorded date so latest dose is first
                            const sortedByDateDesc = [...validRecords].sort(
                              (a, b) => b.parsedTime - a.parsedTime,
                            );
                            const sortedRecords = sortedByDateDesc.length
                              ? sortedByDateDesc
                              : records;
                            const v = sortedRecords.length
                              ? sortedRecords[0]
                              : null;
                            let displayNextDue = v?.next_due;
                            const latestDateGiven =
                              v?.rawDate ||
                              v?.date_given ||
                              v?.date ||
                              v?.given_date;
                            if (!displayNextDue && latestDateGiven) {
                              displayNextDue = addMonths(latestDateGiven, 11);
                            }
                            const expired = isExpired(displayNextDue);
                            return (
                              <tr
                                key={name}
                                className={`${
                                  idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                                } ${expired ? "text-red-600" : ""} hover:bg-indigo-50 transition`}
                                title={expired ? "Vaccine is expired" : undefined}
                              >
                                <td className="px-6 py-4 font-medium text-gray-900">
                                  <div className="flex items-center gap-2.5 flex-wrap">
                                    <span
                                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${
                                        expired
                                          ? "border-red-500 bg-red-50 text-red-700"
                                          : v
                                          ? "border-green-500 bg-green-50 text-green-700"
                                          : "border-gray-300 bg-white text-gray-700"
                                      }`}
                                      title={expired ? "Vaccine is expired" : undefined}
                                      aria-label={expired ? `${name}, vaccine is expired` : name}
                                    >
                                      {v && (
                                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-600 text-white text-xs">
                                          ✓
                                        </span>
                                      )}
                                      <span>{name}</span>
                                    </span>

                                    {sortedRecords.length > 1 && (
                                      <span
                                        className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold border border-indigo-200"
                                        title={`${sortedRecords.length} records/doses recorded for ${name}`}
                                      >
                                        {sortedRecords.length} doses
                                      </span>
                                    )}

                                    <button
                                      type="button"
                                      onClick={() => openVaccineDrawer(name)}
                                      className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all shadow-xs cursor-pointer flex-shrink-0"
                                      title={`Add new dose / booster for ${name}`}
                                      aria-label={`Add new dose for ${name}`}
                                    >
                                      <Plus size={14} strokeWidth={2.5} />
                                    </button>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-gray-700">
                                  {v ? (
                                    <span className="font-medium text-gray-900 text-sm">
                                      {v.date_given ? formatDate(v.date_given) : "-"}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td className={`px-6 py-4 ${expired ? "text-red-600 font-medium" : "text-gray-700"}`}>
                                  {v ? (displayNextDue ? formatDate(displayNextDue) : "-") : "-"}
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex gap-2 flex-wrap">
                                    {sortedRecords.flatMap((r) => r.documents || []).length > 0 ? (
                                      sortedRecords
                                        .flatMap((r) => r.documents || [])
                                        .map((doc, i) => {
                                          const isPdf =
                                            doc.name?.toLowerCase().endsWith(".pdf") ||
                                            doc.url?.toLowerCase().includes(".pdf");
                                          return (
                                            <div key={i} title={doc.name || "Vaccination certificate"}>
                                              <div
                                                onClick={() => setFileViewUrl(doc.url)}
                                                className="h-12 w-12 border rounded-lg cursor-pointer shadow-sm hover:shadow-md transition bg-white flex items-center justify-center overflow-hidden"
                                              >
                                                {isPdf ? (
                                                  <span className="text-xs font-semibold text-red-600">PDF</span>
                                                ) : (
                                                  <img
                                                    src={doc.url}
                                                    alt={doc.name}
                                                    className="h-full w-full object-cover"
                                                  />
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })
                                    ) : (
                                      <span className="text-xs text-gray-400">-</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Add Vaccine Button below the list on the left side */}
                  <div className="flex justify-start mt-3">
                    <button
                      type="button"
                      onClick={() => openCreateVaccineDrawer()}
                      className="text-indigo-600 hover:text-indigo-800 font-bold text-sm transition cursor-pointer py-1"
                    >
                      + Add New Vaccine
                    </button>
                  </div>
                </div>
              )}

              {/* Medical Condition */}
              {healthTab === "medical" && (
                
                <div className="mt-4">
                   {/* Selected conditions as chips */}
                    {(newPet.medical_conditions || []).length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-2 font-medium">Selected conditions:</p>
                        <div className="flex flex-wrap gap-2">
                          {(newPet.medical_conditions || []).map((condition) => (
                            <Chip
                              key={condition}
                              label={condition}
                              variant="outlined"
                              size="small"
                              onDelete={() => removeMedicalCondition(condition)}
                              sx={{ borderColor: '#4f46e5', color: '#4f46e5' }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  
                  <p className="mb-3 mt-3 text-sm text-gray-600">
                    Select existing or create new medical conditions. Click a condition to toggle it.
                  </p>
                  
                  <div className="space-y-3">
                    {/* Single search input */}
                    <div className="relative">
                      <input
                        type="text"
                        value={medicalConditionQuery}
                        onChange={(e) => {
                          setMedicalConditionQuery(e.target.value);
                          setMedicalConditionListOpen(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const trimmed = medicalConditionQuery.trim();
                            if (trimmed) addMedicalCondition(trimmed);
                          }
                        }}
                        placeholder="Search or type to create a new condition..."
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                      />
                      {medicalConditionQuery && (
                        <button
                          type="button"
                          onClick={() => setMedicalConditionQuery("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    {/* Conditions list - always visible */}
                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                      <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                        {filteredMedicalConditionOptions.length > 0 ? (
                          filteredMedicalConditionOptions.map((condition) => {
                            const isSelected = (newPet.medical_conditions || []).includes(condition);
                            return (
                              <button
                                key={condition}
                                type="button"
                                onClick={() => addMedicalCondition(condition)}
                                className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center justify-between gap-2 ${
                                  isSelected
                                    ? "bg-indigo-50 text-indigo-700 font-semibold"
                                    : "hover:bg-gray-50 text-gray-700"
                                }`}
                              >
                                <span>{condition}</span>
                                {isSelected && (
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white text-xs flex-shrink-0">
                                    ✓
                                  </span>
                                )}
                              </button>
                            );
                          })
                        ) : (
                          <div className="px-4 py-4 text-sm text-gray-500 flex flex-col gap-2">
                            <span>No matching conditions found.</span>
                            {medicalConditionQuery.trim() && (
                              <button
                                type="button"
                                onClick={() => addMedicalCondition(medicalConditionQuery.trim())}
                                className="text-left text-indigo-600 font-medium hover:underline"
                              >
                                + Create "{medicalConditionQuery.trim()}"
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                   
                  </div>
                </div>
              )}
               {/* Medication Tab */}
               {healthTab === "medication" && (
                 <div className="mt-4 space-y-4">
                   <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-gray-100">
                     <p className="text-xs md:text-sm text-gray-600">
                       Manage active and previous medications. Active medications represent current treatment, while discontinued medications are preserved in history.
                     </p>
                     <button
                       type="button"
                       onClick={() => setShowAddMedForm((prev) => !prev)}
                       className="self-start sm:self-auto px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition flex items-center gap-1.5 whitespace-nowrap"
                     >
                       <Plus className="w-3.5 h-3.5" />
                       <span>{showAddMedForm ? "Cancel" : "Add Medication"}</span>
                     </button>
                   </div>

                   {/* Add Medication Inline Form */}
                   {showAddMedForm && (
                     <div className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-200 shadow-sm space-y-3">
                       <h5 className="text-xs font-bold uppercase tracking-wider text-indigo-900">
                         New Medication Details
                       </h5>
                       <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                         <div className="sm:col-span-2">
                           <label className="block text-xs font-semibold text-gray-700 mb-1">
                             Medication Name <span className="text-red-500">*</span>
                           </label>
                           <input
                             type="text"
                             value={newMedData.name}
                             onChange={(e) =>
                               setNewMedData((prev) => ({
                                 ...prev,
                                 name: e.target.value,
                               }))
                             }
                             placeholder="e.g. NexGard, Amoxicillin"
                             className="w-full p-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 bg-white"
                           />
                         </div>
                         <div>
                           <label className="block text-xs font-semibold text-gray-700 mb-1">
                             Dosage
                           </label>
                           <input
                             type="text"
                             value={newMedData.dosage}
                             onChange={(e) =>
                               setNewMedData((prev) => ({
                                 ...prev,
                                 dosage: e.target.value,
                               }))
                             }
                             placeholder="e.g. 1 Tablet, 5ml"
                             className="w-full p-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 bg-white"
                           />
                         </div>
                         <div>
                           <label className="block text-xs font-semibold text-gray-700 mb-1">
                             Time / Schedule
                           </label>
                           <select
                             value={newMedData.time}
                             onChange={(e) =>
                               setNewMedData((prev) => ({
                                 ...prev,
                                 time: e.target.value,
                               }))
                             }
                             className="w-full p-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 bg-white"
                           >
                             <option value="Morning">Morning</option>
                             <option value="Afternoon">Afternoon</option>
                             <option value="Evening">Evening</option>
                             <option value="Night">Night</option>
                             <option value="Twice Daily">Twice Daily</option>
                             <option value="With Food">With Food</option>
                             <option value="As Needed">As Needed</option>
                           </select>
                         </div>
                         <div className="sm:col-span-2">
                           <label className="block text-xs font-semibold text-gray-700 mb-1">
                             Duration
                           </label>
                           <input
                             type="text"
                             value={newMedData.duration}
                             onChange={(e) =>
                               setNewMedData((prev) => ({
                                 ...prev,
                                 duration: e.target.value,
                               }))
                             }
                             placeholder="e.g. 7 days, Ongoing"
                             className="w-full p-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 bg-white"
                           />
                         </div>
                         <div className="sm:col-span-2">
                           <label className="block text-xs font-semibold text-gray-700 mb-1">
                             Instructions / Notes
                           </label>
                           <input
                             type="text"
                             value={newMedData.notes}
                             onChange={(e) =>
                               setNewMedData((prev) => ({
                                 ...prev,
                                 notes: e.target.value,
                               }))
                             }
                             placeholder="e.g. Give after food"
                             className="w-full p-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 bg-white"
                           />
                         </div>
                       </div>
                       <div className="flex justify-end gap-2 pt-2">
                         <button
                           type="button"
                           onClick={() => setShowAddMedForm(false)}
                           className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition"
                         >
                           Cancel
                         </button>
                         <button
                           type="button"
                           onClick={handleAddNewMedication}
                           className="px-4 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition"
                         >
                           Add to Pet
                         </button>
                       </div>
                     </div>
                   )}

                   {/* List of Medications */}
                   {(newPet.ongoing_medications || []).length > 0 ? (
                     <div className="space-y-3">
                       {newPet.ongoing_medications.map((med, idx) => {
                         const isActive = med.active !== false;
                         return (
                           <div
                             key={`med-${idx}`}
                             className={`p-4 rounded-xl border shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3 ${
                               isActive
                                 ? "bg-white border-green-200 ring-1 ring-green-100"
                                 : "bg-gray-50/80 border-gray-200 opacity-80"
                             }`}
                           >
                             <div className="space-y-1">
                               <div className="flex items-center gap-2">
                                 <span className="font-semibold text-gray-900 text-base">
                                   {med.name || "Unnamed Medication"}
                                 </span>
                                 <span
                                   className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                                     isActive
                                       ? "bg-green-100 text-green-800 border border-green-200"
                                       : "bg-gray-200 text-gray-700 border border-gray-300"
                                   }`}
                                 >
                                   {isActive
                                     ? "Active (Current)"
                                     : "Discontinued (Previous)"}
                                 </span>
                               </div>
                               <div className="text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
                                 {med.dosage && (
                                   <span>
                                     <strong>Dosage:</strong> {med.dosage}
                                   </span>
                                 )}
                                 {med.time && (
                                   <span>
                                     <strong>When:</strong> {med.time}
                                   </span>
                                 )}
                                 {med.duration && (
                                   <span>
                                     <strong>Duration:</strong> {med.duration}
                                   </span>
                                 )}
                               </div>
                               {med.notes && (
                                 <p className="text-xs text-gray-500 italic mt-1">
                                   {med.notes}
                                 </p>
                               )}
                             </div>
                             <div className="flex items-center gap-2 flex-wrap">
                               {Array.isArray(med.documents) &&
                                 med.documents.length > 0 && (
                                   <div className="flex items-center gap-1.5">
                                     {med.documents.map((doc, dIdx) => (
                                       <a
                                         key={`doc-${dIdx}`}
                                         href={doc.url}
                                         target="_blank"
                                         rel="noopener noreferrer"
                                         className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-md transition"
                                       >
                                         <FileText className="w-3 h-3 text-indigo-600" />
                                         <span>
                                           {doc.name || "Prescription / Photo"}
                                         </span>
                                         <ExternalLink className="w-2.5 h-2.5 text-indigo-500" />
                                       </a>
                                     ))}
                                   </div>
                                 )}

                               {/* Action Buttons: Discontinue / Reactivate and Delete */}
                               <button
                                 type="button"
                                 onClick={() => handleToggleMedicationStatus(idx)}
                                 className={`px-2.5 py-1 text-xs font-medium rounded-lg border transition ${
                                   isActive
                                     ? "text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-300"
                                     : "text-green-700 bg-green-50 hover:bg-green-100 border-green-300"
                                 }`}
                               >
                                 {isActive ? "Discontinue" : "Reactivate"}
                               </button>
                               <button
                                 type="button"
                                 onClick={() => handleDeleteMedication(idx)}
                                 className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                 title="Delete medication"
                               >
                                 <Trash2 className="w-4 h-4" />
                               </button>
                             </div>
                           </div>
                         );
                       })}
                     </div>
                   ) : (
                     <div className="p-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200 text-gray-500 text-sm space-y-2">
                       <p>No medications recorded for this pet.</p>
                       <button
                         type="button"
                         onClick={() => setShowAddMedForm(true)}
                         className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline"
                       >
                         <Plus className="w-3.5 h-3.5" />
                         Add First Medication
                       </button>
                     </div>
                   )}
                 </div>
               )}


              {/* Appearance */}
              {healthTab === "appearance" && (
                <div className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Hair Type */}
                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        Hair Type
                      </label>
                      <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                        {hairTypeOptions.map((opt) => (
                          <label
                            key={opt}
                            className="flex items-center gap-2 cursor-pointer text-sm"
                          >
                            <input
                              type="radio"
                              name="addPetHairType"
                              value={opt}
                              checked={newPet.hair_type === opt}
                              onChange={() =>
                                handleFieldChange("hair_type", opt)
                              }
                              className="accent-indigo-600"
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Hair Length */}
                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        Hair Length
                      </label>
                      <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                        {hairLengthOptions.map((opt) => (
                          <label
                            key={opt}
                            className="flex items-center gap-2 cursor-pointer text-sm"
                          >
                            <input
                              type="radio"
                              name="addPetHairLength"
                              value={opt}
                              checked={newPet.hair_length === opt}
                              onChange={() =>
                                handleFieldChange("hair_length", opt)
                              }
                              className="accent-indigo-600"
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Hair Color */}
                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        Hair Color
                      </label>
                      <div className="rounded-lg border border-gray-200 p-3 space-y-2 max-h-64 overflow-y-auto">
                        {hairColorOptions.map((opt) => {
                          const isSelected = (newPet.color || []).some(
                            (c) => c.toLowerCase() === opt.name.toLowerCase(),
                          );
                          return (
                            <label
                              key={opt.name}
                              className="flex items-center justify-between p-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition text-sm"
                            >
                              <div className="flex items-center gap-2.5">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {
                                    const current = Array.isArray(newPet.color)
                                      ? newPet.color
                                      : [];
                                    const updated = isSelected
                                      ? current.filter(
                                          (c) =>
                                            c.toLowerCase() !==
                                            opt.name.toLowerCase(),
                                        )
                                      : [...current, opt.name];
                                    handleFieldChange("color", updated);
                                  }}
                                  className="accent-indigo-600 rounded cursor-pointer"
                                />
                                <span
                                  className="w-4 h-4 rounded-full border border-gray-300 shadow-xs shrink-0"
                                  style={{ backgroundColor: opt.hex }}
                                />
                                <span className="font-medium text-gray-700">
                                  {opt.name}
                                </span>
                              </div>
                              {isSelected && (
                                <span className="text-xs font-semibold text-indigo-600">
                                  Selected
                                </span>
                              )}
                            </label>
                          );
                        })}

                        {/* Any custom color added by user */}
                        {(newPet.color || [])
                          .filter(
                            (colorStr) =>
                              !hairColorOptions.some(
                                (opt) =>
                                  opt.name.toLowerCase() ===
                                  colorStr.toLowerCase(),
                              ),
                          )
                          .map((colorStr, idx) => (
                            <div
                              key={`custom-${idx}`}
                              className="flex items-center justify-between p-1.5 rounded-lg bg-gray-50 text-sm"
                            >
                              <div className="flex items-center gap-2.5">
                                <span
                                  className="w-4 h-4 rounded-full border border-gray-300 shrink-0"
                                  style={{ backgroundColor: colorStr }}
                                />
                                <span className="font-medium text-gray-700">
                                  {colorStr}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeColor(colorStr)}
                                className="text-gray-400 hover:text-red-500 text-xs px-1 font-bold"
                                title="Remove color"
                              >
                                ×
                              </button>
                            </div>
                          ))}

                        {/* Custom Color Picker */}
                        <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                          <span className="text-xs text-gray-500 font-medium">
                            Custom color:
                          </span>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={customColor}
                              onChange={(e) => setCustomColor(e.target.value)}
                              className="w-7 h-7 p-0 border border-gray-300 rounded cursor-pointer"
                              title="Pick custom color"
                            />
                            <button
                              type="button"
                              onClick={addCustomColor}
                              className="flex items-center justify-center w-6 h-6 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition"
                              title="Add custom color"
                            >
                              <Plus size={13} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Diet */}
              {healthTab === "diet" && (
                <div className="mt-4">
                  <p className="mb-3 text-sm text-gray-600">
                    Please select your preferred diet details.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Meals per day
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={newPet.meals_per_day || ""}
                        onChange={(e) =>
                          handleFieldChange("meals_per_day", e.target.value)
                        }
                        placeholder="e.g. 2"
                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Walks per day
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={newPet.walks_per_day || ""}
                        onChange={(e) =>
                          handleFieldChange("walks_per_day", e.target.value)
                        }
                        placeholder="e.g. 3"
                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Meal TYPES
                    </label>
                    <div className="p-4 rounded-xl border border-gray-200 bg-white">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-10">
                        <div className="space-y-4">
                          {[
                            { label: "Vegetarian", icon: "veg" },
                            { label: "Non-Vegetarian", icon: "nonveg" },
                            { label: "Home-Cooked Diet", icon: "veg" },
                          ].map((item) => (
                            <label
                              key={item.label}
                              className="flex items-center gap-3 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={(newPet.meal_type || []).includes(
                                  item.label,
                                )}
                                onChange={() => {
                                  let updated = [...(newPet.meal_type || [])];
                                  if (updated.includes(item.label)) {
                                    updated = updated.filter(
                                      (t) => t !== item.label,
                                    );
                                  } else {
                                    updated.push(item.label);
                                  }
                                  handleFieldChange("meal_type", updated);
                                }}
                                className="w-4 h-4 accent-indigo-600"
                              />
                              {item.icon === "veg" ? (
                                <span className="w-3 h-3 rounded-full bg-green-600 border border-green-800"></span>
                              ) : (
                                <span className="w-3 h-3 rounded-full bg-red-600 border border-red-800"></span>
                              )}
                              <span className="text-gray-700 text-sm md:text-base">
                                {item.label}
                              </span>
                            </label>
                          ))}
                        </div>
                        <div className="space-y-4">
                          {[
                            { label: "Mixed Diet", icon: "nonveg" },
                            { label: "Prescription Diet", icon: "veg" },
                            { label: "Raw Diet", icon: "nonveg" },
                          ].map((item) => (
                            <label
                              key={item.label}
                              className="flex items-center gap-3 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={(newPet.meal_type || []).includes(
                                  item.label,
                                )}
                                onChange={() => {
                                  let updated = [...(newPet.meal_type || [])];
                                  if (updated.includes(item.label)) {
                                    updated = updated.filter(
                                      (t) => t !== item.label,
                                    );
                                  } else {
                                    updated.push(item.label);
                                  }
                                  handleFieldChange("meal_type", updated);
                                }}
                                className="w-4 h-4 accent-indigo-600"
                              />
                              {item.icon === "veg" ? (
                                <span className="w-3 h-3 rounded-full bg-green-600 border border-green-800"></span>
                              ) : (
                                <span className="w-3 h-3 rounded-full bg-red-600 border border-red-800"></span>
                              )}
                              <span className="text-gray-700 text-sm md:text-base">
                                {item.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Feeding Schedule & Details */}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-gray-800">
                            Current Feeding Schedule & Details
                          </h4>
                          <span className="text-[11px] font-medium bg-green-100 text-green-800 px-2 py-0.5 rounded-full border border-green-200">
                            Active (Current)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowAddMealForm((prev) => !prev)}
                          className="self-start sm:self-auto px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition flex items-center gap-1.5 whitespace-nowrap"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span>{showAddMealForm ? "Cancel" : "Add Meal"}</span>
                        </button>
                      </div>

                      {/* Reminder if meals_per_day doesn't match current meal count */}
                      {Number(newPet.meals_per_day) > (newPet.meals || []).length && (
                        <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-center justify-between">
                          <span>
                            <strong>Note:</strong> Meals per day is set to{" "}
                            <strong>{newPet.meals_per_day}</strong>, but only{" "}
                            <strong>{(newPet.meals || []).length}</strong> meal(s) are configured in the schedule.
                          </span>
                          {!showAddMealForm && (
                            <button
                              type="button"
                              onClick={() => setShowAddMealForm(true)}
                              className="ml-2 underline font-semibold hover:text-amber-900 whitespace-nowrap"
                            >
                              + Add Meal #{ (newPet.meals || []).length + 1 }
                            </button>
                          )}
                        </div>
                      )}

                      {/* Inline Add Meal Form */}
                      {showAddMealForm && (
                        <div className="mb-4 p-4 bg-indigo-50/50 rounded-xl border border-indigo-200 shadow-sm space-y-3">
                          <h5 className="text-xs font-bold uppercase tracking-wider text-indigo-900">
                            Add Meal to Schedule
                          </h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Meal Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={newMealData.name}
                                onChange={(e) =>
                                  setNewMealData((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                  }))
                                }
                                placeholder="e.g. Lunch, Kibble, Dinner"
                                className="w-full p-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 bg-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Time of Day
                              </label>
                              <select
                                value={newMealData.time}
                                onChange={(e) =>
                                  setNewMealData((prev) => ({
                                    ...prev,
                                    time: e.target.value,
                                  }))
                                }
                                className="w-full p-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 bg-white"
                              >
                                <option value="Morning">Morning</option>
                                <option value="Afternoon">Afternoon</option>
                                <option value="Evening">Evening</option>
                                <option value="Night">Night</option>
                                <option value="Mid-day">Mid-day</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Quantity / Portion
                              </label>
                              <input
                                type="text"
                                value={newMealData.quantity}
                                onChange={(e) =>
                                  setNewMealData((prev) => ({
                                    ...prev,
                                    quantity: e.target.value,
                                  }))
                                }
                                placeholder="e.g. 100gm, 1 cup"
                                className="w-full p-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 bg-white"
                              />
                            </div>
                            <div className="sm:col-span-2 md:col-span-3">
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Feeding Instructions / Notes
                              </label>
                              <input
                                type="text"
                                value={newMealData.notes}
                                onChange={(e) =>
                                  setNewMealData((prev) => ({
                                    ...prev,
                                    notes: e.target.value,
                                  }))
                                }
                                placeholder="e.g. Soak in warm water for 5 mins"
                                className="w-full p-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 bg-white"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => setShowAddMealForm(false)}
                              className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleAddNewMeal}
                              className="px-4 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition"
                            >
                              Add Meal
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Meals list */}
                      {(newPet.meals || []).length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {newPet.meals.map((meal, mIdx) => (
                            <div
                              key={`meal-${mIdx}`}
                              className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-1 relative group"
                            >
                              <div className="flex items-center justify-between pr-6">
                                <div className="text-sm font-semibold text-gray-800">
                                  {meal.name || "Scheduled Meal"}
                                </div>
                                <span className="text-[11px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
                                  Added: {meal.added_date ? new Date(meal.added_date).toLocaleDateString() : (newPet.createdAt ? new Date(newPet.createdAt).toLocaleDateString() : "Active")}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600 flex gap-3">
                                {meal.time && (
                                  <span>
                                    <strong>Time:</strong> {meal.time}
                                  </span>
                                )}
                                {meal.quantity && (
                                  <span>
                                    <strong>Quantity:</strong> {meal.quantity}
                                  </span>
                                )}
                              </div>
                              {meal.notes && (
                                <div className="text-xs text-gray-500 italic">
                                  {Array.isArray(meal.notes)
                                    ? meal.notes.join(", ")
                                    : meal.notes}
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteMeal(mIdx)}
                                className="absolute top-2.5 right-2.5 text-gray-400 hover:text-red-600 p-1 rounded-md hover:bg-red-50 transition"
                                title="Remove meal"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-6 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200 text-gray-500 text-sm space-y-2">
                          <p>No meals currently configured in schedule.</p>
                          <button
                            type="button"
                            onClick={() => setShowAddMealForm(true)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Add First Meal
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Meal History (Previous feeding details preserved upon form/drawer update) */}
                    {Array.isArray(newPet.meal_history) &&
                      newPet.meal_history.length > 0 && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                            <span>Previous Meal History</span>
                            <span className="text-[11px] font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full border border-gray-200">
                              Archived (Previous)
                            </span>
                          </h4>
                          <div className="space-y-3">
                            {newPet.meal_history.map((hist, hIdx) => (
                              <div
                                key={`hist-${hIdx}`}
                                className="p-3 bg-gray-50/80 rounded-xl border border-gray-200 text-xs text-gray-600 space-y-2"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-2 text-gray-500">
                                  <span>
                                    <strong>Archived on:</strong>{" "}
                                    {hist.archived_at
                                      ? new Date(
                                          hist.archived_at,
                                        ).toLocaleDateString()
                                      : "Previous Schedule"}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    {hist.meals_per_day && (
                                      <span>
                                        Meals/day: {hist.meals_per_day}
                                      </span>
                                    )}
                                    {hist.walks_per_day && (
                                      <span>
                                        Walks/day: {hist.walks_per_day}
                                      </span>
                                    )}
                                    {hist.source && (
                                      <span className="text-[10px] text-gray-400 uppercase tracking-wider">
                                        ({hist.source.replace("_", " ")})
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {Array.isArray(hist.meal_type) &&
                                  hist.meal_type.length > 0 && (
                                    <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                                      <span className="font-semibold text-gray-700">
                                        Diet / Meal Type:
                                      </span>
                                      {hist.meal_type.map((t, tIdx) => (
                                        <span
                                          key={tIdx}
                                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200"
                                        >
                                          {t}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                {Array.isArray(hist.meals) &&
                                  hist.meals.length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                                      {hist.meals.map((hm, hmIdx) => (
                                        <div
                                          key={`hm-${hmIdx}`}
                                          className="p-2 bg-white rounded border border-gray-200"
                                        >
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="font-medium text-gray-800">
                                              {hm.name || "Meal"}
                                              {hm.time && (
                                                <span className="text-gray-500 font-normal"> ({hm.time})</span>
                                              )}
                                            </span>
                                            {hm.added_date && (
                                              <span className="text-[10px] text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">
                                                Added: {new Date(hm.added_date).toLocaleDateString()}
                                              </span>
                                            )}
                                          </div>
                                          {hm.quantity && (
                                            <div>Qty: {hm.quantity}</div>
                                          )}
                                          {hm.notes && (
                                            <div className="italic text-gray-500">
                                              {hm.notes}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>

            {/* Pet Behavior / Notes (Moved to very end of drawer) */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <label className="block text-sm font-semibold mb-1">
                {formConfig.labelMap["pet.behavior_notes"] ||
                  formConfig.labelMap["pet.behaviour"] ||
                  "Pet Behavior & Notes"}
              </label>
              <textarea
                rows={3}
                value={newPet.behavior_notes || ""}
                onChange={(e) =>
                  handleFieldChange("behavior_notes", e.target.value)
                }
                placeholder="Enter any behavior notes, social tendencies, or special handling instructions..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Bottom Buttons */}
          <div
            className="absolute bottom-0 left-0 right-0 bg-white px-4 py-3 border-t border-gray-200 shadow-md"
            style={{ zIndex: 1000 }}
          >
            <div
              className={`grid grid-cols-1 sm:gap-4 gap-4 ${
                !petId && !shouldHideSaveAndAdd
                  ? "sm:grid-cols-3"
                  : "sm:grid-cols-2"
              }`}
            >
              <button
                onClick={() => {
                  resetForm();
                  handleClose();
                }}
                className="w-full py-3 font-bold text-base border border-gray-300 rounded-xl hover:bg-gray-100"
                disabled={loading}
              >
                Cancel
              </button>
              {!petId && !shouldHideSaveAndAdd && (
                <button
                  onClick={async () => {
                    const errors = validateForm(filteredPetSizes);
                    setPetFormErrors(errors);
                    if (Object.keys(errors).length > 0) {
                      toast.warn("Please fill all required fields", {
                        position: "top-center",
                      });
                      return;
                    }

                    const saved = await handleAddNewPet(newPet, false);
                    if (saved) {
                      resetForm();
                      setFormVisible(true);
                      setFormReady(true);
                    }
                  }}
                  className="w-full py-3 font-bold text-base bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
                  disabled={loading}
                >
                  Save & Add New Pet
                </button>
              )}
              <button
                onClick={handleNewPetSave}
                className="w-full py-3 font-bold text-base bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
                disabled={loading}
              >
                {!petId ? "Save" : "Update"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ===== VACCINE DRAWER (inside AddNewPet) ===== */}
      {showVaccineDrawer && currentVaccineRecord && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[998]"
            onClick={closeVaccineDrawer}
          />
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col z-[999]">
            <div className="p-5 border-b flex justify-between items-center bg-gray-50">
              <h2 className="text-xl font-semibold">
                {vaccineDrawerMode === "createMaster"
                  ? "Add New Vaccine"
                  : "Add Vaccine"}
              </h2>
              <X className="cursor-pointer w-6 h-6" onClick={closeVaccineDrawer} />
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              {vaccineDrawerMode === "createMaster" ? (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Vaccine Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newMasterVaccineName}
                    onChange={(e) => setNewMasterVaccineName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (!addingVaccineLoading && newMasterVaccineName.trim()) {
                          handleAddMasterVaccine();
                        }
                      }
                    }}
                    placeholder="Enter vaccine name..."
                    autoFocus
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  />
                </div>
              ) : (
                <>
                  <div className="space-y-2 relative" ref={vaccineDropdownRef}>
                    <label className="block text-sm font-semibold text-gray-700">
                      Vaccine Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={vaccineSearchQuery}
                        onChange={(e) => {
                          const val = e.target.value;
                          setVaccineSearchQuery(val);
                          setVaccineDropdownOpen(true);
                          handleVaccineInput("vaccine", val);
                        }}
                        onFocus={() => setVaccineDropdownOpen(true)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const query = vaccineSearchQuery.trim();
                            if (!query) return;
                            const exactMatch = vaccinationOptions.find(
                              (opt) => opt.toLowerCase() === query.toLowerCase(),
                            );
                            if (exactMatch) {
                              handleVaccineInput("vaccine", exactMatch);
                              setVaccineSearchQuery(exactMatch);
                              setVaccineDropdownOpen(false);
                            } else {
                              handleCreateCustomVaccine(query);
                            }
                          }
                        }}
                        placeholder="Search or type custom vaccine..."
                        className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white pr-14"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-gray-400">
                        {vaccineSearchQuery && (
                          <button
                            type="button"
                            onClick={() => {
                              setVaccineSearchQuery("");
                              handleVaccineInput("vaccine", "");
                              setVaccineDropdownOpen(true);
                            }}
                            className="hover:text-gray-600 p-0.5 rounded-full"
                            title="Clear"
                          >
                            <X size={16} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setVaccineDropdownOpen((prev) => !prev)}
                          className="hover:text-gray-600 p-0.5"
                        >
                          <ChevronDown
                            size={18}
                            className={`transition-transform duration-200 ${
                              vaccineDropdownOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Dropdown Menu */}
                    {vaccineDropdownOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                        {filteredVaccineOptions.length > 0 ? (
                          <div className="py-1 divide-y divide-gray-50">
                            {filteredVaccineOptions.map((opt) => {
                              const isSelected =
                                currentVaccineRecord.vaccine?.toLowerCase() ===
                                opt.toLowerCase();
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  onClick={() => {
                                    handleVaccineInput("vaccine", opt);
                                    setVaccineSearchQuery(opt);
                                    setVaccineDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${
                                    isSelected
                                      ? "bg-indigo-50 text-indigo-700 font-semibold"
                                      : "hover:bg-gray-50 text-gray-700"
                                  }`}
                                >
                                  <span>{opt}</span>
                                  {isSelected && (
                                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white text-xs">
                                      ✓
                                    </span>
                                  )}
                                </button>
                              );
                            })}

                            {/* If query has text and doesn't exactly match any existing option, offer to create it */}
                            {vaccineSearchQuery.trim() &&
                              !vaccinationOptions.some(
                                (o) =>
                                  o.toLowerCase() ===
                                  vaccineSearchQuery.trim().toLowerCase(),
                              ) && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleCreateCustomVaccine(
                                      vaccineSearchQuery.trim(),
                                    )
                                  }
                                  disabled={addingVaccineLoading}
                                  className="w-full text-left px-4 py-3 text-sm font-medium text-indigo-600 bg-indigo-50/70 hover:bg-indigo-100 flex items-center gap-2 transition-colors border-t border-indigo-100"
                                >
                                  <Plus
                                    size={16}
                                    className="text-indigo-600 flex-shrink-0"
                                  />
                                  <span>
                                    {addingVaccineLoading
                                      ? "Adding vaccine..."
                                      : `Create "${vaccineSearchQuery.trim()}"`}
                                  </span>
                                </button>
                              )}
                          </div>
                        ) : (
                          <div className="p-4 text-sm text-gray-500 flex flex-col gap-2">
                            <span>No matching vaccines found.</span>
                            {vaccineSearchQuery.trim() && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleCreateCustomVaccine(
                                    vaccineSearchQuery.trim(),
                                  )
                                }
                                disabled={addingVaccineLoading}
                                className="text-left text-indigo-600 font-medium hover:underline flex items-center gap-1.5"
                              >
                                <Plus size={15} />
                                <span>
                                  {addingVaccineLoading
                                    ? "Adding vaccine..."
                                    : `Create "${vaccineSearchQuery.trim()}"`}
                                </span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label>Date Given</label>
                    <input
                      type="date"
                      value={currentVaccineRecord.date_given}
                      onChange={(e) => handleVaccineInput("date_given", e.target.value)}
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </div>

                  <div className="space-y-2">
                    <label>Next Due (Optional)</label>
                    <input
                      type="date"
                      value={currentVaccineRecord.next_due}
                      onChange={(e) => handleVaccineInput("next_due", e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </div>

                  <div className="space-y-2">
                    <label>Upload Files</label>
                    <div
                      className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer ${
                        isDraggingVaccineFile
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-gray-300"
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDraggingVaccineFile(true);
                      }}
                      onDragLeave={() => setIsDraggingVaccineFile(false)}
                      onDrop={async (e) => {
                        e.preventDefault();
                        setIsDraggingVaccineFile(false);
                        await handleVaccineFileUpload(Array.from(e.dataTransfer.files));
                      }}
                      onClick={() => vaccineFileInputRef.current?.click()}
                    >
                      <Upload className="text-indigo-600 mx-auto mb-2" size={24} />
                      <p className="font-medium text-indigo-600">
                        Click or Drag files to upload
                      </p>
                    </div>
                    <input
                      ref={vaccineFileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={(e) => handleVaccineFileUpload(Array.from(e.target.files))}
                    />

                    <div className="grid grid-cols-3 gap-3 mt-4">
                      {currentVaccineRecord.documents.map((doc, i) => {
                        const isPdf =
                          doc.name?.toLowerCase().endsWith(".pdf") ||
                          doc.url?.toLowerCase().includes(".pdf");
                        return (
                          <div key={i} className="relative border rounded-lg p-1">
                            <div
                              onClick={() => setFileViewUrl(doc.url)}
                              className="h-20 w-full cursor-pointer bg-gray-50 flex items-center justify-center overflow-hidden"
                            >
                              {isPdf ? (
                                <span className="text-sm font-medium">PDF</span>
                              ) : (
                                <img
                                  src={doc.url}
                                  alt={doc.name}
                                  className="h-full w-full object-cover"
                                />
                              )}
                            </div>
                            <button
                              onClick={() =>
                                setCurrentVaccineRecord((prev) => ({
                                  ...prev,
                                  documents: prev.documents.filter((_, idx) => idx !== i),
                                }))
                              }
                              className="absolute -top-2 -right-2 h-6 w-6 bg-red-600 text-white rounded-full flex items-center justify-center"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {vaccineDrawerMode === "createMaster" ? (
              <div className="p-4 border-t flex gap-3 bg-gray-50">
                <button
                  type="button"
                  onClick={closeVaccineDrawer}
                  className="w-1/2 border py-3 rounded-xl hover:bg-gray-100 font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddMasterVaccine}
                  disabled={addingVaccineLoading || !newMasterVaccineName.trim()}
                  className="w-1/2 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:bg-gray-400 transition"
                >
                  {addingVaccineLoading ? "Adding..." : "Add New Vaccine"}
                </button>
              </div>
            ) : (
              <div className="p-4 border-t flex gap-3 bg-gray-50">
                <button
                  onClick={closeVaccineDrawer}
                  className="w-1/2 border py-3 rounded-xl hover:bg-gray-100 font-medium transition"
                >
                  Cancel
                </button>
                <button
                  onClick={saveVaccineRecord}
                  disabled={
                    !currentVaccineRecord.vaccine ||
                    currentVaccineRecord.vaccine === "Select Vaccine"
                  }
                  className="w-1/2 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:bg-gray-400 transition"
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* FILE VIEWER MODAL */}
      {fileViewUrl && (
        <FileViewerModal url={fileViewUrl} onClose={() => setFileViewUrl(null)} />
      )}
    </div>
  );
};

export default AddNewPet;
